import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Divider,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Field } from "formik";
import { DatePicker } from "antd";
import moment from "moment";
import { nomineeRelations, relianceNomineeRelations, nationalNomineeRelations, nomineeTypeOptions } from "../constants/formOptions";
import {
  hasNomineeAge,
  isOtherRelation,
  isKshema,
  isNational,
  isReliance,
  BRISK,
  sameCompany,
  paCompanyOffered,
  buildPolicySettings,
  settingsToLegacyFields,
  routingOf,
  coverageOf,
  hasCompanyLeg,
  nomineeFieldsOf,
  splitFromCalculation,
  chargeBasisOf,
  describeParts,
} from "../utils/companyRules";
import { visibleRtoOptions } from "../utils/rtoVisibility";
import { useOnlinePolicyConfig } from "../hooks/useOnlinePolicyConfig";
import { useMasterDrift } from "../hooks/useMasterDrift";
import {
  computeMasterDrift,
  missingVehicleFromSnapshot,
  sameValue,
} from "../utils/masterDrift";
import { months } from "../OptionsData";
import { GetMakeModel, GetMakeModelCombo, GetRtoCityLocation, GetUserFindbyId, GetZone, GetNationalNominee, GetFormula } from "../../../../Service/_index";
import { ToastError, ToastSuccess } from "../../../../UiComponents/Toaster/Toast";
import { midpointIdv, resolveIdvRange, setActiveIdvFormula } from "../utils/idvHelpers";
import { preserveCaret } from "../utils/inputHelpers";
import MotorCalculationCard, { PremiumFooter } from "./MotorCalculationCard";
import { CompanyPanel } from "./CompanyPremiumCards";
import {
  toVehicleOption,
  comboOption,
  visibleCombos,
  routingFor,
  tpVehicleOf,
  paFloorOf,
  tppdAvailableOf,
  premiumTotals,
} from "../utils/vehicleSelection";
import MasterDriftNotice from "./MasterDriftNotice";
import PolicyIdvCalculatorDialog from "./PolicyIdvCalculatorDialog";
import { appliedValues, byHandChanges, fromSellingPrice, IDV_FORMULA_NAME, masterCalc, rupees } from "../utils/idvByHand";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import { stashPrefill, prefillUrl } from "../utils/prefillHandoff";
import { CHOICE_TOGGLE_SX, SECTION_TITLE_SX, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX, TOOL_BUTTON_SX, NAVY, FONT } from "./formStyles";
import FormSection, { FormSubsection } from "./FormSection";

/** National's portal takes the PA term as 1 or 5 years ("No of Years" in Compulsory PA). */
const NATIONAL_PA_YEARS = [
  { label: "1 Year", value: 1 },
  { label: "5 Years", value: 5 },
];

/**
 * Basic Policy Details Step Component
 * First step of the online policy form
 */
// Long-standing floor for the PA Cover amount, used when the Make & Model
// master does not set a higher one for the vehicle.
const DEFAULT_PA_COVER_MIN = 422;
const BRISK_PA_COVER_MIN = 177;

// One shared geometry for the Zero Depreciation / TPPD / PA Cover rows.
//
// Each label is a different length ("Zero Depreciation" vs "PA Cover"), so
// letting the fields simply follow the label started every row at a different
// x-position. A fixed-width checkbox column lines the rows up, and
// flex-start stops the checkbox floating to the middle of a tall field stack.
// A row of options: the checkbox in its own column on the left, its fields in even columns beside it. When the fields
// do not fit on one line the next one goes under the first FIELD (not back under the checkbox). On a phone everything
// is one per line. The fields' own fixed widths give way to the columns.
const OPTION_ROW_SX = {
  display: "grid",
  // the rows hug their content: the checkbox spans six rows, and stretched empty rows left a gap under PA Cover
  alignContent: "start",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "200px repeat(auto-fill, minmax(230px, 1fr))" },
  columnGap: 3,
  rowGap: 0,
  alignItems: "start",
  "& > .MuiBox-root": { width: "100% !important", maxWidth: { sm: 420 }, minWidth: 0 },
  // the checkbox column runs down beside every line of fields
  "& > .MuiBox-root:first-of-type": { gridRow: { sm: "1 / span 6" }, mb: { xs: 1, sm: 0 } },
};
const OPTION_LABEL_SX = { width: { xs: "100%", sm: 200 }, flexShrink: 0, pt: 0.5 };
const OPTION_FIELD_WIDTH = 240;
// a group inside a section card ("Nominee Details", "PA Cover Details"): smaller than the card's own title
const SUBHEAD_SX = { fontFamily: FONT, fontSize: "14px", fontWeight: 700, color: NAVY, lineHeight: "20px" };
// the label of a control that is not a text box (the Company tabs): the same look the form's field labels have
const FIELD_LABEL_SX = { fontFamily: FONT, fontSize: "13.5px", fontWeight: 600, color: "#344054" };

const BasicPolicyDetailsStep = ({
  values,
  errors,
  touched,
  setFieldValue,
  selectedMakeModel,
  setSelectedMakeModel,
  // Multiple company: the Combined Make & Model record picked. selectedMakeModel
  // stays the OD row; the TP row is selectedCombo.tpRow.
  selectedCombo = null,
  setSelectedCombo,
  filteredVehicleData,
  filterVehicleData,
  onTppdRestrictChange,
  onZeroDepreciationChange,
  formik,
  calculationResult,
  showCalculationResult,
  setShowCalculationResult,
  // Edit only: what the policy was saved with, and a way to stop keeping the
  // saved vehicle / RTO once the operator changes it. Null when creating, which
  // makes every guard below a no-op.
  savedSnapshot = null,
  releaseSaved,
}) => {
  const [makeModelData, setMakeModelData] = useState([]);
  const [filteredMakeModelData, setFilteredMakeModelData] = useState([]);
  const [idvRangeHint, setIdvRangeHint] = useState("");
  const [idvError, setIdvError] = useState("");
  // The IDV Calculator beside the IDV field (this policy only - utils/idvByHand.js)
  const [idvCalcOpen, setIdvCalcOpen] = useState(false);

  // Calculate worked: the page scrolls down to the premium by itself (the owner, 2026-10-27). Only when the result
  // newly appears - an edited policy that opens with its saved premium on screen stays at the top.
  const premiumRef = useRef(null);
  const premiumWasShown = useRef(!!showCalculationResult);
  useEffect(() => {
    const appeared = !!showCalculationResult && !premiumWasShown.current;
    premiumWasShown.current = !!showCalculationResult;
    if (!appeared) return undefined;
    const timer = setTimeout(() => {
      const el = premiumRef.current;
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50); // after the section's contents have laid out
    return () => clearTimeout(timer);
  }, [showCalculationResult]);

  // The saved IDV formula (Make & Model -> IDV Calculator), so a selling price typed here gives the same range the
  // master would. A failed / missing fetch keeps the defaults (5% / 95% / 105%).
  useEffect(() => {
    let alive = true;
    Promise.resolve()
      .then(() => GetFormula(IDV_FORMULA_NAME))
      .then((res) => {
        if (alive && res && res.data && res.data.values) setActiveIdvFormula(res.data.values);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const [openBreakup, setOpenBreakup] = useState(false);
  const [dynamicRtoLocations, setDynamicRtoLocations] = useState([]);
  // Combined Make & Model records of this client (Multiple company mode).
  const [comboOptions, setComboOptions] = useState([]);

  // ── Per-user Make & Model discounts ───────────────────────────────────────
  // A CLIENT can assign specific make/models to a user with that user's own
  // discount (User.makemodelids). When the logged-in user has any, the
  // dropdown is limited to them and their discount wins over the master's.
  const [userDiscountMap, setUserDiscountMap] = useState({});
  // Every make/model assigned to this user, whether or not they switched on
  // their own discount for it. Drives which options are selectable.
  const [userMakeModelIds, setUserMakeModelIds] = useState(new Set());
  // Combined Make & Models the admin assigned to this user (see visibleCombos).
  const [userComboIds, setUserComboIds] = useState(new Set());

  // ── Per-user RTO City/Location assignments ────────────────────────────────
  // User.rtocityids. Same rule as make/model: a user with NONE assigned sees the
  // whole master; once any are assigned, only those (see visibleRtoOptions).
  // null = not loaded yet (no UserId in the session means nothing to load).
  const [userRtoIds, setUserRtoIds] = useState(() =>
    sessionStorage.getItem("UserId") ? null : new Set()
  );
  const isMasterAdmin =
    String(sessionStorage.getItem("userType") || "").toUpperCase() === "CLIENT";

  // Which saved values this edited policy still holds on to (all false when creating).
  const { keepVehicle, keepRto, keeps } = useMasterDrift(savedSnapshot, values);

  useEffect(() => {
    const userId = sessionStorage.getItem("UserId");
    if (!userId) return;
    let cancelled = false;

    GetUserFindbyId(userId)
      .then((res) => {
        if (cancelled) return;
        setUserRtoIds(
          new Set(
            (res?.data?.rtocityids || [])
              .filter((row) => row?.rtoCity)
              .map((row) => String(row.rtoCity))
          )
        );
        const rows = res?.data?.makemodelids || [];
        // Assignment and discount-override are SEPARATE concerns:
        //  - being assigned a make/model decides whether it is selectable
        //  - the useOwnDiscount switch decides whose discount applies
        // Deriving visibility from the discount map hid every assigned record
        // the user had not switched an override on for.
        const assigned = new Set();
        const map = {};
        for (const row of rows) {
          if (!row?.makemodel) continue;
          assigned.add(String(row.makemodel));
          // Only an explicit opt-in overrides the master discount. Merely being
          // assigned a make/model must not change the price.
          if (row.useOwnDiscount !== true) continue;
          const value = Number(row.Discount);
          // 0 is a legitimate discount, so only skip genuinely absent values.
          if (Number.isFinite(value)) map[String(row.makemodel)] = value;
        }
        setUserMakeModelIds(assigned);
        // Combined Make & Models assigned to this user (Edit User > Combined Make & Model).
        setUserComboIds(
          new Set(
            (res?.data?.makemodelcomboids || [])
              .filter((row) => row?.makeModelCombo)
              .map((row) => String(row.makeModelCombo))
          )
        );
        setUserDiscountMap(map);
        if (Object.keys(map).length > 0) {
          console.log(
            `Per-user Make & Model discounts loaded: ${Object.keys(map).length} entry(ies)`
          );
        }
      })
      .catch(() => {
        // No per-user discounts available — fall back to the master.
        if (!cancelled) {
          setUserDiscountMap({});
          setUserMakeModelIds(new Set());
          setUserComboIds(new Set());
          setUserRtoIds(new Set());
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Restrict the dropdown only once we know the user actually has assignments;
  // a user with none keeps the full list (unlike RTO, which is strict).
  const hasUserAssignments = userMakeModelIds.size > 0;

  /**
   * The discount to use for a Make & Model record: the logged-in user's own
   * value when they have one, otherwise the master's. Returns "" when neither
   * is set, so the field falls back to manual entry.
   */
  const resolveDiscount = (record) => {
    if (!record?._id) return "";
    const userValue = userDiscountMap[String(record._id)];
    if (Number.isFinite(userValue)) return String(userValue);
    return record.discount !== undefined && record.discount !== null
      ? String(record.discount)
      : "";
  };

  /** National keeps its IDV editable in model-not-found mode; Reliance does not. */
  const isNationalCompany = (company) =>
    String(company || "").trim().toLowerCase() === "national";

  /**
   * The IDV range to hint and validate against: the range an edited policy was
   * saved with while it is kept, otherwise the master's. `respectSaved` is
   * false for handlers that have just released the saved vehicle — their
   * closure still says "kept" until the next render.
   */
  const idvRangeFor = (record, respectSaved = true) =>
    respectSaved && keeps("idvRangeFrom") && keeps("idvRangeTo")
      ? {
          from: String(savedSnapshot.idvRangeFrom),
          to: String(savedSnapshot.idvRangeTo),
        }
      : resolveIdvRange(record);

  /**
   * Copy the Make & Model master's "model not found" configuration onto the
   * form, deriving the IDV range from the selling price.
   *
   * When set, the portal is driven through its own "Not able to Find the
   * required Model & Variant" option. For Reliance that means the portal
   * derives the IDV itself, so `idv` is stored as 0 and the field is disabled.
   * National operators enter the IDV by hand instead, inside the range the
   * selling price implies — so the field stays open there and this leaves
   * whatever the form already holds.
   *
   * `respectSaved`: an edited policy keeps the values it was saved with (see
   * useMasterDrift); only fields it has no saved value for take the master's.
   */
  const applyModelNotFound = (record, { respectSaved = false } = {}) => {
    const keep = (field) => respectSaved && keeps(field);
    const flagged = !!record?.modelNotFound;
    if (!keep("modelNotFound")) setFieldValue("modelNotFound", flagged);

    if (!flagged) {
      // Carry the selling price through even when the flag is off. Reliance
      // records use it purely as the source of the IDV range (Make & Model
      // master derives 90%–99% from it), so blanking it here would drop a
      // figure the master legitimately holds.
      if (!keep("manufacturerSellingPrice")) {
        setFieldValue(
          "manufacturerSellingPrice",
          record?.manufacturerSellingPrice || ""
        );
      }
      // Selling price wins over the stored range when the record has one.
      const { from, to } = resolveIdvRange(record);
      if (!keep("idvRangeFrom")) setFieldValue("idvRangeFrom", from);
      if (!keep("idvRangeTo")) setFieldValue("idvRangeTo", to);
      return;
    }

    const price = Number(record?.manufacturerSellingPrice) || 0;
    // The same resolver every other range reads. This branch used to hardcode
    // 90%–99% of the price, so a record whose stored range had been adjusted on
    // purpose was validated against a range nothing else in the form used.
    const { from, to } = resolveIdvRange(record);

    if (!keep("manufacturerSellingPrice")) {
      setFieldValue("manufacturerSellingPrice", price || "");
    }
    if (!keep("idvRangeFrom")) setFieldValue("idvRangeFrom", from);
    if (!keep("idvRangeTo")) setFieldValue("idvRangeTo", to);

    // Reliance's portal derives the IDV from the selling price and ours is
    // never sent, so 0 is stored. National's is typed by the operator: leave
    // the field alone here so neither the saved policy's IDV (edit) nor a
    // figure already entered is thrown away — handleMakeModelChange seeds it
    // when the vehicle is actually picked. A kept saved flag keeps its IDV too.
    if (!isNationalCompany(record?.company) && !keep("modelNotFound")) {
      setFieldValue("idv", 0);
    }

    // Any range error from a previous selection no longer applies, and the
    // idvError -> errors.idv bridge would otherwise keep it pinned.
    setIdvError("");
    const shown = idvRangeFor(record, respectSaved);
    if (shown.from !== "" && shown.to !== "") {
      setIdvRangeHint(`${shown.from} to ${shown.to}`);
    }
  };

  /**
   * The selling price the master implies, normalised EXACTLY the way
   * applyModelNotFound writes it, so the effect below can compare the two
   * without ever disagreeing with itself and looping.
   */
  const masterSellingPrice = (record) =>
    record?.modelNotFound
      ? (Number(record?.manufacturerSellingPrice) || 0) || ""
      : record?.manufacturerSellingPrice || "";

  /**
   * Re-assert the master's model-not-found configuration on the form.
   *
   * Both fields are OWNED by the Make & Model master — nothing in this form
   * lets anyone type them — so putting them back is always safe.
   *
   * They used to be refreshed only inside the id-mismatch branch of the
   * make/model sync effect below, which left two holes:
   *
   *  - Edit: Formik mounts already holding the saved makeModel, so that sync
   *    fires as soon as the master list arrives — and then handleEditPolicy's
   *    100 ms setValues REPLACES the whole values object, dropping both fields
   *    (its map never listed them). By then the ids match, so the branch would
   *    not run again: the IDV field came up enabled with no selling price.
   *    Whether it broke came down to whether the master fetch beat that 100 ms
   *    timer — which is exactly why it worked some times and not others.
   *  - Prefill from the Make & Model page: selectedMakeModel is set with the
   *    matching _id from the start, so the branch never ran at all.
   *
   * National hard-fails without the price ("modelNotFound is set but no
   * manufacturerSellingPrice was supplied" — national.js), so rather than rely
   * on one lucky pass, restore whenever the form drifts from the master.
   */
  useEffect(() => {
    if (!selectedMakeModel?._id) return;
    // A selling price typed for this policy (IDV & Selling Price section / IDV Calculator) stays.
    if (values.idvByHand) return;

    const flagged = !!selectedMakeModel.modelNotFound;
    const price = String(masterSellingPrice(selectedMakeModel));

    // Agrees with the master — or is a saved value an edited policy keeps.
    if (
      (keeps("modelNotFound") || !!values.modelNotFound === flagged) &&
      (keeps("manufacturerSellingPrice") ||
        String(values.manufacturerSellingPrice ?? "") === price)
    ) {
      return;
    }

    applyModelNotFound(selectedMakeModel, { respectSaved: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMakeModel,
    values.modelNotFound,
    values.manufacturerSellingPrice,
    values.idvByHand,
    keepVehicle,
  ]);

  /**
   * Is the IDV field out of the operator's hands?
   *
   * Only for Reliance. Its portal computes the IDV from the selling price in
   * model-not-found mode, so a typed figure would be a lie. National's operator
   * enters the IDV by hand within the range the selling price implies, so the
   * field stays open and required there — see the schema's matching rule.
   */
  const idvLockedByPortal =
    !!values.modelNotFound && !isNationalCompany(values.company);

  /**
   * Auto-fill the IDV with the MIDDLE of its range — 100 to 200 gives 150.
   *
   * handleMakeModelChange already seeds this when a vehicle is picked by hand.
   * Nothing did on the paths that set the make/model programmatically (edit,
   * prefill from the Make & Model page), and a National model-not-found policy
   * saved while its IDV field was still locked holds a 0 that now fails the
   * range check — so the operator had to work the figure out themselves.
   *
   * Seeded ONCE per vehicle, tracked by _id rather than by watching the field:
   * a plain "is it empty" effect would refill the moment someone cleared the
   * box to retype it, and they could never finish typing. A value already on
   * the form — typed by hand, or restored from a saved policy — is left alone,
   * and the seed is skipped entirely while the range is still resolving so a
   * later arrival still gets its turn.
   */
  const idvSeededFor = useRef(null);

  useEffect(() => {
    const vehicleId = selectedMakeModel?._id
      ? String(selectedMakeModel._id)
      : null;
    if (!vehicleId || idvSeededFor.current === vehicleId) return;

    // Reliance stores 0 and the portal works the IDV out for itself.
    if (idvLockedByPortal) {
      idvSeededFor.current = vehicleId;
      return;
    }

    const current = Number(values.idv);
    if (Number.isFinite(current) && current > 0) {
      idvSeededFor.current = vehicleId; // already has a real value
      return;
    }

    const mid = midpointIdv(values.idvRangeFrom, values.idvRangeTo);
    if (!mid) return; // range not resolved yet — retry when it lands

    setFieldValue("idv", mid);
    setIdvError("");
    idvSeededFor.current = vehicleId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMakeModel,
    idvLockedByPortal,
    values.idv,
    values.idvRangeFrom,
    values.idvRangeTo,
  ]);

  // Discount comes from the Make & Model master (or the user's own override);
  // only show the field when neither has a value, so it can be entered manually
  const discountAutoFilled = resolveDiscount(selectedMakeModel) !== "";

  // ── Per-vehicle Zero Depreciation and TPPD ────────────────────────────────
  // Both come from the selected Make & Model row. The NillDip master and the
  // "Zero Depreciation Value" dropdown it fed are retired.

  /** The master's Nil Dip Price for the selected vehicle, or "" when unset. */
  const masterNilDip =
    selectedMakeModel?.nilDipPrice === undefined ||
      selectedMakeModel?.nilDipPrice === null ||
      selectedMakeModel?.nilDipPrice === ""
      ? ""
      : String(selectedMakeModel.nilDipPrice);

  // When the master has a value the field is read-only; when it does not, the
  // user types one and it is written back after the policy is created.
  const masterHasNilDip = masterNilDip !== "";

  /**
   * Single company (one Make & Model row) or Multiple company (a Combined Make &
   * Model record: the OD insurer's row and the TP insurer's row). `selectedMakeModel`
   * is the OD row either way, so everything that follows it is unchanged.
   */
  const mode = values.companyChoice === "multiple" ? "multiple" : "single";
  /**
   * What the policy covers. OD only / TP only exist for ONE company (Single); Multiple company is always OD + TP.
   * "od": own damage alone — no TPPD, no PA inside the premium. "tp": third party alone — no IDV, discount or Zero
   * Depreciation. The field itself is `values.coverage` (companyRules.coverageOf).
   */
  const coverage = mode === "single" ? coverageOf(values) : "both";
  /** OD only / TP only show ONE panel (like Multiple company's two) for the insurer picked from the Make & Model. */
  const showPartPanel = mode === "single" && coverage !== "both";
  /** The TP insurer's vehicle (Multiple only). It prices Total B. */
  const tpRow = tpVehicleOf({ mode, combo: selectedCombo });

  /**
   * The OD / TP panels hold the side-owned fields (IDV, discount, Zero Depreciation;
   * TPPD) in Multiple mode, so they show as soon as there is something to price: a
   * chosen record, or an old multi-company policy opened for edit (no record; it
   * keeps what it was sold with).
   */
  const showSidePanels = mode === "multiple" && (!!selectedCombo || !!values._legacyMultiple);
  /** A wide screen: the OD / TP panels sit on the right (onlinePolicyForm.scss, the same 900px). */
  const wideForm = useMediaQuery("(min-width:900px)");

  /** TPPD is offered only for vehicles whose master row switches it on — the TP row in Multiple (it is a third-party item). */
  const tppdAvailable = coverage !== "od" && tppdAvailableOf({ mode, odRow: selectedMakeModel, tpRow });

  /**
   * Company routing. `values.settings` ({ isMultipleCompany, PACompany,
   * ODCompany, TPCompany }) is the single source of truth; company, odCompany,
   * tpCompany, paCoverCompany and paCoverInsurerCompany are derived from it by
   * writeRouting below — nothing else sets them.
   *
   * The PA Cover Company dropdown offers Brisk plus an insurer of the policy:
   * its one company, or — sold by two — the TP insurer only, because PA Cover
   * is not taken on the OD insurer's policy (companyRules.paCompanyOffered).
   * Only when the server runs OD and TP as separate jobs (multiProductPolicy)
   * can the TP insurer sell PA; otherwise one bundled job is sold by the OD
   * company. "Brisk" creates the Brisk CPA/RSA certificate; any insurer means
   * it is bought with that insurer's policy and no Brisk certificate is made.
   */
  const { multiProductPolicy, coverChoice, briskPaCover: briskPaConfig } = useOnlinePolicyConfig();
  const briskPaCover = briskPaConfig !== false;
  const routing = routingOf(values, { multiProduct: multiProductPolicy });
  // An edited policy keeps the PA company it was sold with on the list, even
  // one that is no longer offered (PA on the OD insurer of a two-insurer policy).
  const savedPaCompany = savedSnapshot?.paCover ? savedSnapshot?.routing?.PACompany || "" : "";
  const paCompanyChoices = paCompanyOffered(routing, {
    multiProduct: multiProductPolicy,
    saved: savedPaCompany,
    brisk: briskPaCover,
  });
  // Where a PA Cover starts: Brisk, or — with Brisk switched off on the server — the policy's first offered insurer.
  const defaultPaFor = (s) =>
    briskPaCover
      ? BRISK
      : paCompanyOffered(s, { multiProduct: multiProductPolicy, saved: savedPaCompany, brisk: false })[0] || "";
  // Every company that COULD provide PA, including the TP insurer even when it
  // cannot be picked yet. The dropdown shows the unavailable ones greyed out
  // with the reason, rather than silently leaving them off the list.
  const paCompanyAll = [
    ...paCompanyChoices,
    ...paCompanyOffered(routing, { multiProduct: true, saved: savedPaCompany, brisk: briskPaCover }).filter(
      (c) => !paCompanyChoices.some((o) => sameCompany(o, c))
    ),
  ];
  const paCompanyLocked = paCompanyAll.filter(
    (c) => !paCompanyChoices.some((o) => sameCompany(o, c))
  );
  const isBriskPaCover = sameCompany(routing.PACompany, BRISK);

  /**
   * Whose nominee questions the PA Cover Details section asks, and which of them (utils/companyRules.js nomineeFieldsOf — the
   * same answer the validation uses). PA through Brisk on two insurers / TP only is Brisk's own: Name, Gender, Relation from
   * Brisk's eight.
   */
  const nominee = nomineeFieldsOf(values);
  const nomineeCompany = nominee.company;
  /** Nominee Age / years / default are National's, whichever side it is on. */
  const hasNationalLeg = hasCompanyLeg(values, isNational);
  /**
   * A National TP asks the PA term (1 or 5 years) ONLY when PA Cover is ticked and bought from National: no default,
   * it must be picked, in PA Cover Details (the `paCoverYears` field that was always there). With PA Cover off the
   * portal's "Compulsory PA for Owner Driver" is "No" and nothing is asked; with PA Cover from Brisk it stays "No"
   * too (Brisk's certificate is the cover), so no year is needed there either.
   */
  const nationalTpYears =
    (showSidePanels || coverage === "tp") &&
    isNational(routing.TPCompany) &&
    !!values.paCover &&
    isNational(routing.PACompany);

  /**
   * The ONE writer of the routing fields. Takes the two master companies and
   * (optionally) the wanted PA company, rebuilds `settings`, and re-derives
   * every flat field from it so they can never disagree. A PA company that is
   * no longer offered (vehicle changed) falls back to Brisk's default.
   */
  const writeRouting = ({ od, tp, pa, paCover = values.paCover }) => {
    const next = buildPolicySettings(
      {
        paCover,
        paCoverCompany: values.paCoverCompany,
        paCoverInsurerCompany: values.paCoverInsurerCompany,
        settings: { ODCompany: od, TPCompany: tp, PACompany: pa },
        company: od,
      },
      { multiProduct: multiProductPolicy }
    );
    // With PA off there is no PACompany yet; keep the default so switching PA
    // on starts from Brisk (paCoverCompany's long-standing default).
    if (!paCover && !next.PACompany) next.PACompany = defaultPaFor(next) || BRISK;
    // Two insurers: a PA company that is not offered for them — the OD insurer,
    // carried over from a single-company pick or reached as the fallback when
    // the vehicle changed — goes back to Brisk, the default.
    if (
      paCover &&
      next.PACompany &&
      !paCompanyOffered(next, {
        multiProduct: multiProductPolicy,
        saved: savedPaCompany,
        brisk: briskPaCover,
      }).some((c) => sameCompany(c, next.PACompany))
    ) {
      next.PACompany = defaultPaFor(next) || BRISK;
    }
    setFieldValue("settings", next);
    const flat = settingsToLegacyFields(next, { paCover });
    Object.entries(flat).forEach(([k, v]) => setFieldValue(k, v));
    // OD only / TP only: one product is selected (the server derives it again from `coverage`).
    setFieldValue("odSelected", coverage !== "tp");
    setFieldValue("tpSelected", coverage !== "od");
    // Several setFieldValue calls in one handler each validate against the same
    // stale snapshot; one clean pass once the state has settled.
    setTimeout(() => formik?.validateForm?.(), 0);
    return next;
  };

  // Brisk switched off on the server but the form still holds it (the default it started with): move to the
  // first offered insurer. An edited policy keeps the Brisk it was sold with (paCompanyOffered's `saved`).
  useEffect(() => {
    if (briskPaCover || !values.paCover || !sameCompany(routing.PACompany, BRISK)) return;
    if (paCompanyChoices.some((c) => sameCompany(c, BRISK)) || !paCompanyChoices.length) return;
    writeRouting({ od: routing.ODCompany, tp: routing.TPCompany, pa: paCompanyChoices[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briskPaCover, values.paCover, routing.PACompany, routing.ODCompany, routing.TPCompany]);

  /**
   * OD only (Reliance's own-damage product) has no PA Cover and no nominee: the portal's OD form shows neither. So the
   * Coverage / PA Cover box is not drawn and PA is forced off (a nominee is asked only with PA Cover, or for National).
   * An old OD-only policy saved WITH PA keeps the box, so its PA can still be seen and switched off by hand.
   * Multiple company is not affected: PA and the nominee there belong to the TP company (paCompanyOffered).
   */
  const hidePaCover = coverage === "od" && !savedSnapshot?.paCover;
  useEffect(() => {
    if (!hidePaCover || !values.paCover) return;
    setFieldValue("paCover", false);
    setFieldValue("paCoverAmount", "");
    setFieldValue("paCoverCompany", "");
    setFieldValue("notPaCoverReason", "");
    writeRouting({
      od: routing.ODCompany,
      tp: routing.TPCompany,
      pa: defaultPaFor(routing) || BRISK,
      paCover: false,
    });
    if (setShowCalculationResult) setShowCalculationResult(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidePaCover, values.paCover]);

  /**
   * Fill the Zero Depreciation value from the master.
   *
   * Only overwrites when the field is empty or still holds a DIFFERENT
   * master value — a figure typed by hand for a vehicle with no master value
   * must survive, since that is exactly what gets saved back.
   */
  useEffect(() => {
    if (!values.zeroDepreciation || !masterHasNilDip) return;
    // An edited policy keeps the Nil Dip price it was sold with.
    if (keeps("zeroDepreciationPercentage")) return;
    if (String(values.zeroDepreciationPercentage || "") === masterNilDip) return;

    setFieldValue("zeroDepreciationPercentage", masterNilDip);
    if (setShowCalculationResult) setShowCalculationResult(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterNilDip, masterHasNilDip, values.zeroDepreciation, keepVehicle]);

  /**
   * Record whether the Nil Dip value came from the master.
   *
   * useOnlinePolicyForm only writes back to the Make & Model master when it did
   * NOT — otherwise every policy would pointlessly re-save the same figure.
   * UI-only: the payload builder picks fields explicitly, so this never reaches
   * the backend.
   */
  useEffect(() => {
    if (values._nilDipFromMaster !== masterHasNilDip) {
      setFieldValue("_nilDipFromMaster", masterHasNilDip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterHasNilDip, values._nilDipFromMaster]);

  /**
   * Brisk's minimum is always ₹177, whatever the vehicle (the owner, 2026-10-31). The Make & Model minimum (blank = ₹422)
   * is for a PA sold by an insurer only.
   */
  const savedPaAmount = Number(savedSnapshot?.paCoverAmount);
  const vehiclePaFloorForCompany = (company) =>
    sameCompany(company, BRISK) ? BRISK_PA_COVER_MIN : paFloorOf([selectedMakeModel, tpRow], DEFAULT_PA_COVER_MIN);
  // The saved amount speaks only for the company it was sold with: a Brisk ₹177 moved to an insurer needs the insurer's minimum.
  const soldWithPaCompany = (company) => !savedPaCompany || sameCompany(savedPaCompany, company);
  // An edited policy keeps its saved amount if a newly raised master floor would reject it.
  const savedAmountBelowFloor = (company) =>
    keepVehicle &&
    !!savedSnapshot?.paCover &&
    soldWithPaCompany(company) &&
    savedPaAmount > 0 &&
    savedPaAmount < vehiclePaFloorForCompany(company);
  const paFloorForCompany = (company) =>
    savedAmountBelowFloor(company) ? savedPaAmount : vehiclePaFloorForCompany(company);
  const masterPaFloor = vehiclePaFloorForCompany(routing.PACompany);
  const paFloorLoweredBySaved = savedAmountBelowFloor(routing.PACompany);
  const paCoverMinAmount = paFloorForCompany(routing.PACompany);
  const fillBriskPaMinimum = (company) => {
    if (!sameCompany(company, BRISK)) return;
    const currentAmount = Number(values.paCoverAmount);
    const minimum = paFloorForCompany(BRISK);
    setFieldValue("_paCoverMinAmount", minimum);
    if (!Number.isFinite(currentAmount) || currentAmount < minimum) {
      setFieldValue("paCoverAmount", minimum);
    }
  };
  // Another PA company picked: an amount that is empty, below the new minimum or still on the old minimum goes to the
  // new minimum. A figure typed above it stays.
  const movePaAmountToMinimum = (company) => {
    const amount = Number(values.paCoverAmount);
    const minimum = paFloorForCompany(company);
    if (!Number.isFinite(amount) || amount < minimum || amount === paCoverMinAmount) {
      setFieldValue("paCoverAmount", minimum);
    }
  };

  // Published to the form so the Yup schema can validate against it — a static
  // schema cannot read the selected make/model on its own.
  useEffect(() => {
    if (values._paCoverMinAmount !== paCoverMinAmount) {
      setFieldValue("_paCoverMinAmount", paCoverMinAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paCoverMinAmount, values._paCoverMinAmount]);

  /**
   * Keep TPPD Restrict off for vehicles that do not offer it.
   *
   * initialValues sets it to true, and premiumCalculator adds
   * `tPPDRestrict: "yes"` whenever it is truthy — so leaving it on while the
   * checkbox is hidden would silently change the premium.
   */
  useEffect(() => {
    // Wait until a make/model is actually selected and loaded before deciding to clear it.
    // Otherwise, this runs on edit mode initialization before data loads and wipes out a saved "true".
    // An edited policy keeps the TPPD it was sold with even if the master has
    // since stopped offering it (the checkbox stays visible so it can be unticked).
    if (
      selectedMakeModel &&
      !tppdAvailable &&
      values.tppdRestrict &&
      !keeps("tppdRestrict")
    ) {
      setFieldValue("tppdRestrict", false);
      if (setShowCalculationResult) setShowCalculationResult(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tppdAvailable, values.tppdRestrict, selectedMakeModel, keepVehicle]);

  /**
   * Registration Date follows Purchase Date.
   *
   * The Registration Date input is no longer rendered, but the field is still
   * stored, still sent in the payload and still `required` in the validation
   * schema. Mirroring it here — not only in the date picker's onChange — covers
   * the paths that set purchaseDate programmatically (restoring a policy for
   * edit, prefill), so validation can never fail on a field nobody can see.
   */
  useEffect(() => {
    const purchase = values.purchaseDate || null;
    const registration = values.registrationDate || null;

    const same =
      purchase && registration
        ? new Date(purchase).getTime() === new Date(registration).getTime()
        : purchase === registration;

    if (!same) setFieldValue("registrationDate", purchase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.purchaseDate, values.registrationDate]);

  /**
   * Apply the user's own discount once it arrives.
   *
   * User.makemodelids is fetched asynchronously. A make/model can be resolved
   * before it lands — prefilled from the Make & Model page, or restored while
   * editing — and resolveDiscount would then fall back to the MASTER value and
   * never revisit it, so "Use separate discount" silently did nothing.
   *
   * Only overwrite when the field still holds the master value (or is empty):
   * a figure typed by hand must never be replaced.
   */
  useEffect(() => {
    if (!selectedMakeModel?._id) return;
    // An edited policy keeps the discount it was sold with.
    if (keeps("discount")) return;

    const ownValue = userDiscountMap[String(selectedMakeModel._id)];
    if (!Number.isFinite(ownValue)) return; // no opt-in override for this record

    const own = String(ownValue);
    const master =
      selectedMakeModel.discount !== undefined &&
        selectedMakeModel.discount !== null
        ? String(selectedMakeModel.discount)
        : "";
    const current =
      values.discount === undefined || values.discount === null
        ? ""
        : String(values.discount);

    if (current === own) return; // already correct
    if (current !== "" && current !== master) return; // entered by hand

    setFieldValue("discount", own);
    console.log(
      `Discount switched to this user's own value: ${current || "(empty)"} -> ${own}`
    );
    // The premium, if already calculated, used the previous discount.
    if (setShowCalculationResult) setShowCalculationResult(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDiscountMap, selectedMakeModel, values.discount, keepVehicle]);

  // Debug: Log when component renders with selectedMakeModel
  useEffect(() => {
    console.log(
      "BasicPolicyDetailsStep rendered - selectedMakeModel:",
      selectedMakeModel
    );
    console.log(
      "BasicPolicyDetailsStep rendered - values.makeModel:",
      values.makeModel
    );
  }, [selectedMakeModel, values.makeModel]);

  // Ensure selectedMakeModel is always in the options array
  // OD only is sold by Reliance only (its own-damage product asks the customer's TP policy): only Reliance rows.
  const odOnlyReliance = mode === "single" && coverage === "od";
  const autocompleteOptions = useMemo(() => {
    // Restrict to the make/models assigned to this user, when any are set.
    // Users with no assignments (and other roles) keep the full list.
    const assigned = hasUserAssignments
      ? filteredMakeModelData.filter((item) =>
        userMakeModelIds.has(String(item._id))
      )
      : filteredMakeModelData;
    const allowed = odOnlyReliance ? assigned.filter((item) => isReliance(item.company)) : assigned;

    if (!selectedMakeModel) {
      return allowed;
    }
    // The saved make/model of an existing policy must remain selectable even
    // if it was later un-assigned, otherwise editing that policy would clear
    // the field.
    const isInFiltered = allowed.some(
      (item) => item._id?.toString() === selectedMakeModel._id?.toString()
    );
    if (isInFiltered) {
      return allowed;
    }
    return [selectedMakeModel, ...allowed];
  }, [filteredMakeModelData, selectedMakeModel, userMakeModelIds, hasUserAssignments, odOnlyReliance]);

  // ── Combined Make & Model (Multiple company) ──────────────────────────────
  // The client's records, loaded only while the server runs the multi-company
  // flow. The server answers `enabled: false` when that switch is off.
  useEffect(() => {
    if (!multiProductPolicy) return undefined;
    let cancelled = false;
    GetMakeModelCombo({ isEnabled: true }).then((res) => {
      if (cancelled || !res || res.enabled === false) return;
      setComboOptions((res.data || []).map(comboOption));
    });
    return () => {
      cancelled = true;
    };
  }, [multiProductPolicy]);

  // Same narrowing as the single dropdown: a user with assigned Make & Models sees
  // only the records whose OD Make & Model is assigned to them. The saved policy's
  // own record stays resolvable even if it was disabled or un-assigned since.
  const comboAutocompleteOptions = useMemo(() => {
    const visible = visibleCombos(comboOptions, {
      assignedIds: userMakeModelIds,
      hasAssignments: hasUserAssignments,
      assignedComboIds: userComboIds,
      keepId: values.makeModelCombo,
    });
    if (selectedCombo && !visible.some((o) => String(o._id) === String(selectedCombo._id))) {
      return [selectedCombo, ...visible];
    }
    return visible;
  }, [comboOptions, userMakeModelIds, hasUserAssignments, userComboIds, values.makeModelCombo, selectedCombo]);

  // Memoize Autocomplete values to prevent unnecessary re-renders
  const rtoValue = useMemo(() => {
    if (!values.rtoCityLocation) return null;
    return dynamicRtoLocations.find((rto) => rto.value === values.rtoCityLocation) || null;
  }, [values.rtoCityLocation, dynamicRtoLocations]);

  /**
   * Keep Region / City in step with the RTO City Location master.
   *
   * RTORegion and RTOCity are SNAPSHOTS, written only by the dropdown's
   * onChange below. On edit the dropdown is never touched — the saved code is
   * restored and matches, so onChange never fires — which meant a Region
   * corrected on the master afterwards was never picked up and the update
   * re-saved the old text. The automation types `CITY - REGION` into the
   * insurer portal (national.js), so the stale snapshot travelled all the way
   * into the queued job.
   *
   * Same source-of-truth rule the zone effect below applies. Only writes when
   * the code still matches a master row: a list that has not loaded yet, or a
   * row since deleted, must never blank a saved policy.
   */
  useEffect(() => {
    if (!rtoValue) return;

    const region = rtoValue.region || "";
    const city = rtoValue.city || "";

    // An edited policy keeps the region / city it was saved with.
    if (!keeps("RTORegion") && (values.RTORegion || "") !== region) {
      setFieldValue("RTORegion", region);
    }
    if (!keeps("RTOCity") && (values.RTOCity || "") !== city) {
      setFieldValue("RTOCity", city);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtoValue, values.RTORegion, values.RTOCity, keepRto]);

  // ── Zone-based pricing ────────────────────────────────────────────────────
  // The premium's base rate comes from zoneData[zone][ccBand] — Zone A and
  // Zone B carry different rates. The Online Policy used to hardcode zone "A",
  // so every policy was priced at Zone A regardless of where its RTO sits.
  const [zoneList, setZoneList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    GetZone()
      .then((res) => {
        if (cancelled) return;
        setZoneList(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setZoneList([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The calculator's zone key ("A" / "B") for a zone row.
   *
   * Zone names are free text on the master ("ZONE A", "Zone B", "A"), while
   * the rate table is keyed strictly by the letter — so pull the letter out of
   * the name rather than requiring an exact match.
   */
  const zoneKeyFromName = (zoneName) => {
    const match = /\b([AB])\b/i.exec(String(zoneName || "").trim());
    return match ? match[1].toUpperCase() : null;
  };

  /** The zone the selected RTO City/Location carries, if it has one. */
  const rtoZone = (() => {
    const rto = dynamicRtoLocations.find(
      (r) => r.value === values.rtoCityLocation
    );
    if (!rto?.zoneId) return null;
    return zoneList.find((z) => String(z._id) === String(rto.zoneId)) || null;
  })();

  // Only ask when the RTO cannot answer: an RTO with a zone set drives the
  // premium on its own, and offering a second control would let the two
  // disagree.
  const zoneNeedsChoice = !!values.rtoCityLocation && !rtoZone;

  /**
   * Take the zone from the RTO when it has one.
   *
   * Deliberately does NOT fall back to "A" — that silently priced every
   * zone-less RTO at Zone A rates. When the RTO has no zone the field is left
   * for the user to choose (see the picker below).
   */
  useEffect(() => {
    if (!rtoZone) return;
    // An edited policy keeps the zone it was priced with.
    if (keeps("zone")) return;
    const key = zoneKeyFromName(rtoZone.zoneName);
    if (key && values.zone !== key) {
      setFieldValue("zone", key);
      // The premium was worked out with the previous zone's base rate.
      if (setShowCalculationResult) setShowCalculationResult(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtoZone, values.zone, keepRto]);

  /**
   * The RTO options this user may pick from: the whole master for a CLIENT and
   * for a user with no RTO assigned; only the assigned ones otherwise. The saved
   * RTO of an edited policy stays selectable (utils/rtoVisibility.js).
   */
  const visibleRtoLocations = useMemo(
    () =>
      visibleRtoOptions({
        all: dynamicRtoLocations,
        assignedIds: userRtoIds,
        isMasterAdmin,
        saved: rtoValue,
      }),
    [dynamicRtoLocations, userRtoIds, isMasterAdmin, rtoValue]
  );



  const monthValue = useMemo(() => {
    return months.find((m) => m.value === values.manufacturingMonth) || null;
  }, [values.manufacturingMonth]);

  const nomineeValue = useMemo(() => {
    if (!values.nomineeRelation) return null;
    return nominee.relations.find((rel) => rel.value === values.nomineeRelation?.toLowerCase()) || null;
  }, [values.nomineeRelation, nominee.relations]);
  const nomineeGenderValue = useMemo(() => {
    return values.nomineeGender
      ? nominee.genders.find((rel) => rel.value === values.nomineeGender?.toLowerCase()) || null
      : null;
  }, [values.nomineeGender, nominee.genders]);
  const nomineeTypeValue = useMemo(() => {
    return values.nomineeType
      ? nomineeTypeOptions.find((t) => t.value === values.nomineeType?.toLowerCase()) || null
      : null;
  }, [values.nomineeType]);

  // Each insurer's relation list uses different wording, so a relation carried
  // over from another company is not just cosmetically wrong — it is a value
  // the new portal will reject. Drop it when the list no longer contains it
  // and let the user re-pick, rather than submitting a silent mismatch.
  useEffect(() => {
    if (!values.nomineeRelation) return;
    const stillValid = nominee.relations.some(
      (rel) => rel.value === values.nomineeRelation?.toLowerCase()
    );
    if (!stillValid) setFieldValue("nomineeRelation", "");
  }, [nominee.relations]);

  // Brisk takes Male or Female only: an "Other" picked under another layout is dropped, to be picked again.
  useEffect(() => {
    if (!values.nomineeGender) return;
    if (!nominee.genders.some((g) => g.value === values.nomineeGender?.toLowerCase())) setFieldValue("nomineeGender", "");
  }, [nominee.genders]);

  // National demands a nominee on EVERY quote, PA Cover or not (see
  // validationSchemas.js), but this form only ever asked for one under PA
  // Cover Details. Pull the operator's saved default (Profile -> Account &
  // Policy Settings -> National -> Nominee Details) as a starting point for
  // National policies, whether or not PA Cover is on — the agent can still
  // hand-edit it below for this one policy. `null` means "not fetched yet",
  // an object with empty strings means "fetched, but nothing is saved".
  const [nationalNomineeDefault, setNationalNomineeDefault] = useState(null);
  const nationalNomineeFetchedFor = useRef(null);
  useEffect(() => {
    // Whichever side (OD or TP) National is on, it asks for a nominee.
    if (!hasNationalLeg) return;
    if (nationalNomineeFetchedFor.current === "National") return;
    nationalNomineeFetchedFor.current = "National";

    const userId = sessionStorage.getItem("UserId");
    if (!userId) return;

    GetNationalNominee(userId).then((data) => {
      setNationalNomineeDefault(data);
      // Never overwrite a nominee the policy (or the agent) already has.
      if (data.name && !values.nomineeName) setFieldValue("nomineeName", data.name);
      if (data.relation && !values.nomineeRelation) setFieldValue("nomineeRelation", data.relation);
      if (data.age && !values.nomineeAge) setFieldValue("nomineeAge", data.age);
    });
  }, [hasNationalLeg]);

  // A National TP's PA term starts EMPTY — the form's default of 1 would otherwise be "chosen" by nobody. If it stops
  // being a National TP (another vehicle) and nobody picked, the default comes back, so the other flows are as before.
  // Follows the National TP, not where the field is shown: ticking PA Cover moves it, it does not reset it.
  useEffect(() => {
    if (values._paYearsChosen) return;
    if (nationalTpYears && values.paCoverYears !== "") setFieldValue("paCoverYears", "");
    if (!nationalTpYears && values.paCoverYears === "") setFieldValue("paCoverYears", 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationalTpYears, values._paYearsChosen]);

  // Fetch Make & Model data from API
  useEffect(() => {
    const fetchMakeModelData = async () => {
      try {
        const response = await GetMakeModel();
        if (response && response.data) {
          // Only active records — plus, when editing, the policy's own vehicle
          // even if it was disabled since, so it still resolves (it just cannot
          // be picked again: see getOptionDisabled).
          const ownId = savedSnapshot?.makeModel;
          const formattedData = response.data
            .filter(
              (item) =>
                item.isEnabled || (ownId && String(item._id) === ownId)
            )
            .map((item) => toVehicleOption(item));
          setMakeModelData(formattedData);
          setFilteredMakeModelData(formattedData);
          // The dropdown itself is narrowed to the user's assigned records by
          // visibleMakeModelData below — the full list is kept here so an
          // existing policy can still resolve its saved make/model on edit.
          console.log(
            "MakeModel data loaded in BasicPolicyDetailsStep:",
            formattedData.length,
            "items"
          );
        }
      } catch (error) {
        console.error("Error fetching make-model data:", error);
        ToastError("Failed to fetch Make & Model data");
      }
    };

    fetchMakeModelData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch RTO City/Location data from API
  useEffect(() => {
    const fetchRtoData = async () => {
      try {
        const response = await GetRtoCityLocation();
        console.log("RTO API Response:", response);
        const dataArr = response?.data || (Array.isArray(response) ? response : []);
        const formattedData = dataArr
          .map((item) => {
            const rawCode = (item.RTOCode || item.rtoCity || "").trim().toUpperCase();
            let storedValue = rawCode;
            if (rawCode && !rawCode.includes("-") && rawCode.length >= 4) {
              storedValue = `${rawCode.substring(0, 2)}-${rawCode.substring(2)}`;
            }
            return {
              // Needed to match against the user's assignments, which store a
              // reference to the master row.
              _id: item._id,
              // Drives the premium: the base rate differs per zone.
              zoneId: item.zoneId,
              label: `${rawCode}-${item.Region || item.location || ""}-${item.city || ""}`,
              value: storedValue,
              region: item.Region || item.location || "",
              city: item.city || "",
            };
          });
        console.log("Formatted RTO Data:", formattedData);
        setDynamicRtoLocations(formattedData);
      } catch (error) {
        console.error("Error fetching RTO data:", error);
      }
    };
    fetchRtoData();
  }, []);

  // The NillDip master is retired — the Zero Depreciation value now comes
  // from the selected Make & Model row (see masterNilDip above).


  /**
   * Copy the fields the Make & Model master owns — discount, variant,
   * model-not-found and the IDV range — onto the form, then re-check the IDV
   * against that range.
   *
   *  - respectSaved: an edited policy keeps the values it was saved with.
   *  - reroute: take the master's insurers when the form has none yet (prefill).
   *    A saved policy's insurers are never changed here — that would move
   *    wallet debits and the OD/TP job routing.
   *  - quiet: no toast (the caller reports the outcome itself).
   */
  const refreshFromVehicleMaster = (
    row,
    { reroute = false, quiet = false, respectSaved = false } = {}
  ) => {
    const keep = (field) => respectSaved && keeps(field);

    // Per-user discount first, then the master's.
    const masterDiscount = resolveDiscount(row);
    const currentDiscount =
      values.discount !== undefined && values.discount !== null
        ? String(values.discount)
        : "";

    if (
      !keep("discount") &&
      masterDiscount !== "" &&
      masterDiscount !== currentDiscount
    ) {
      setFieldValue("discount", masterDiscount);
      if (!quiet) {
        // The premium silently changes on save otherwise.
        ToastSuccess(
          `Discount updated from Make & Model master: ${currentDiscount || "—"}% → ${masterDiscount}%`
        );
      }
      // The premium was calculated with the old discount.
      if (setShowCalculationResult) setShowCalculationResult(false);
    }

    // A form with no routing yet (prefill) takes the master's insurers.
    if (reroute) {
      const savedRouting = routingOf(values, { multiProduct: multiProductPolicy });
      if (!savedRouting.ODCompany) {
        // A Make & Model row is one company's: OD and TP are both that company.
        const masterOd = row.company || "";
        writeRouting({ od: masterOd, tp: masterOd, pa: savedRouting.PACompany });
      }
    }

    const masterVariant = row.vehicleVariant || "";
    if (
      !keep("vehicleVariant") &&
      masterVariant &&
      masterVariant !== (values.vehicleVariant || "")
    ) {
      setFieldValue("vehicleVariant", masterVariant);
    }

    // It may have been flagged as "model not found" after this policy was created.
    applyModelNotFound(row, { respectSaved });

    const range = idvRangeFor(row, respectSaved);
    if (range.from && range.to) {
      setIdvRangeHint(`${range.from} to ${range.to}`);
    }

    // The IDV field is about to be zeroed (Reliance model-not-found) — nothing
    // left to validate.
    const idvZeroed =
      !keep("modelNotFound") &&
      !!row.modelNotFound &&
      !isNationalCompany(row.company);

    // An edited master can move the range so the stored IDV is out of bounds.
    if (values.idv && !idvZeroed) {
      validateIDV(values.idv, row, respectSaved);
    }
  };

  // Sync selectedMakeModel when makeModel data loads and form has a makeModel value
  // This effect runs whenever makeModelData, values.makeModel, or selectedMakeModel changes
  useEffect(() => {
    console.log("=== Sync useEffect triggered ===");
    console.log("makeModelData length:", makeModelData.length);
    console.log("values.makeModel:", values.makeModel);
    console.log("selectedMakeModel:", selectedMakeModel);

    // If we have makeModel data and a form value, try to sync
    if (makeModelData.length > 0 && values.makeModel) {
      // Check if selectedMakeModel matches the form value
      const currentMakeModelId = selectedMakeModel?._id?.toString();
      const formMakeModelId = values.makeModel?.toString();

      console.log(
        "Sync check - Current selectedMakeModel ID:",
        currentMakeModelId
      );
      console.log("Sync check - Form makeModel ID:", formMakeModelId);
      console.log(
        "Sync check - MakeModel data available:",
        makeModelData.length
      );

      // If no selectedMakeModel or it doesn't match the form value, find and set it
      if (!selectedMakeModel || currentMakeModelId !== formMakeModelId) {
        console.log(
          "Need to sync - searching for makeModel with ID:",
          formMakeModelId
        );
        const matchingMakeModel = makeModelData.find((item) => {
          const itemId = item._id?.toString();
          const formId = formMakeModelId;
          const matches = itemId === formId;
          if (matches) {
            console.log("Found matching makeModel:", item);
          }
          return matches;
        });
        if (matchingMakeModel && setSelectedMakeModel) {
          console.log("Setting selectedMakeModel to:", matchingMakeModel);
          setSelectedMakeModel(matchingMakeModel);

          // Refresh the fields OWNED by the Make & Model master. When editing, the
          // form holds the SAVED policy, so an edited policy keeps what it was
          // saved with (respectSaved) and useMasterDrift's banner reports what
          // the master says now; anything it has no saved value for falls back
          // to the master as before.
          refreshFromVehicleMaster(matchingMakeModel, {
            reroute: true,
            respectSaved: true,
          });
        } else if (formMakeModelId && setSelectedMakeModel) {
          console.warn("Could not find makeModel with _id:", formMakeModelId);
          console.warn(
            "Available makeModels:",
            makeModelData.slice(0, 5).map((m) => ({
              _id: m._id?.toString(),
              make: m.make,
              model: m.model,
            }))
          );
          // The saved vehicle is gone from the master altogether: stand in for it
          // from the saved policy so the engine CC never falls back to the 160cc
          // default. Only while the vehicle is kept.
          if (keepVehicle && savedSnapshot) {
            setSelectedMakeModel(
              missingVehicleFromSnapshot(savedSnapshot, formMakeModelId)
            );
          }
        }
      } else {
        console.log("selectedMakeModel already matches form value");
      }
    } else if (!values.makeModel && selectedMakeModel) {
      // Clear selectedMakeModel if form value is cleared
      if (setSelectedMakeModel) {
        setSelectedMakeModel(null);
        setIdvRangeHint("");
      }
    } else {
      console.log("Sync skipped - conditions not met:", {
        hasMakeModelData: makeModelData.length > 0,
        hasFormValue: !!values.makeModel,
      });
    }
  }, [
    makeModelData,
    values.makeModel,
    selectedMakeModel,
    setSelectedMakeModel,
    values.idv,
  ]);

  // Keep the IDV range hint in sync with the selected make/model
  // (covers pre-filled selection coming from the Make & Model page)
  useEffect(() => {
    const selectedRange = idvRangeFor(selectedMakeModel);
    if (selectedRange.from && selectedRange.to) {
      setIdvRangeHint(`${selectedRange.from} to ${selectedRange.to}`);
    } else if (!selectedMakeModel) {
      setIdvRangeHint("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMakeModel, keepVehicle]);

  // Sync custom IDV error with Formik errors
  useEffect(() => {
    if (idvError && formik?.setFieldError) {
      // Set custom error using Formik's setFieldError
      formik.setFieldError("idv", idvError);
    } else if (
      !idvError &&
      touched.idv &&
      errors.idv &&
      formik?.validateField
    ) {
      // If custom error is cleared but field is touched, re-validate with Formik
      formik.validateField("idv");
    }
  }, [idvError, touched.idv, errors.idv]);

  // Filter make-model data based on search input.
  //
  // Searches make, model, company, variant, CC and fuel type together, and
  // splits the input on whitespace so EVERY word has to match somewhere —
  // "tata nexon kshema" finds the KSHEMA Nexon in any order. The old version
  // ran a single `includes` against make/model/label only, so neither the
  // company nor the variant was reachable, and a two-word search failed
  // outright unless the words happened to sit next to each other in one field.
  const filterMakeModelData = (searchTerm) => {
    const tokens = String(searchTerm || "")
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!tokens.length) {
      setFilteredMakeModelData(makeModelData);
      return;
    }

    setFilteredMakeModelData(
      makeModelData.filter((item) => {
        const haystack =
          item.searchText ||
          `${item.make || ""} ${item.model || ""}`.toLowerCase();
        return tokens.every((token) => haystack.includes(token));
      })
    );
  };

  // Validate IDV against selected make-model range. `respectSaved` validates
  // against the saved range while an edited policy keeps it.
  const validateIDV = (idvValue, selectedMakeModelItem, respectSaved = false) => {
    if (!selectedMakeModelItem || !idvValue) {
      setIdvError("");
      return true;
    }

    const idvNum = parseFloat(idvValue);
    // Validate against the SAME range the hint and auto-fill use, or a record
    // whose price and stored range disagree would reject its own default.
    const validationRange = idvRangeFor(selectedMakeModelItem, respectSaved);
    const minIdv = parseFloat(validationRange.from);
    const maxIdv = parseFloat(validationRange.to);

    if (isNaN(idvNum)) {
      setIdvError("");
      return true;
    }

    if (idvNum < minIdv || idvNum > maxIdv) {
      const errorMsg = `IDV must be between ${minIdv.toLocaleString()} and ${maxIdv.toLocaleString()}`;
      setIdvError(errorMsg);
      return false;
    }

    setIdvError("");
    return true;
  };

  // Handle make-model selection and set IDV range hint
  /**
   * Pick the vehicle. `selectedItem` is the Make & Model row — in Multiple company
   * mode the OD row of the chosen Combined Make & Model (`combo`), whose TP row is
   * the other vehicle. Null clears the pick.
   */
  const handleMakeModelChange = (selectedItem, { combo = null } = {}) => {
    // This handler sets many fields — makeModel, company, idv among them, all
    // required — through separate setFieldValue calls, and applyModelNotFound
    // adds more of its own. Every one of those validates against the SAME stale
    // snapshot (formik setFieldValue -> setIn(state.values, ...)), so whichever
    // resolves last can leave a required field's error on screen even though it
    // now has a value.
    //
    // Restructuring the whole handler into one setValues would fight
    // applyModelNotFound, so instead force a single clean revalidation once the
    // state has settled. setTimeout, not a microtask: React must flush the
    // updates first.
    setTimeout(() => formik?.validateForm?.(), 0);

    // Whatever is picked (even the same vehicle, or nothing) ends "keep the
    // saved vehicle values" — from here on it behaves like a new policy.
    releaseSaved?.("vehicle");
    // a selling price typed for the previous vehicle does not carry over
    setFieldValue("idvByHand", false);

    setSelectedMakeModel(selectedItem);
    // The record behind this pick (none in Single mode, or when cleared).
    setSelectedCombo?.(combo);
    setFieldValue("makeModelCombo", combo ? String(combo._id) : "");
    setFieldValue("_legacyMultiple", false);
    setFieldValue("makeModel", selectedItem?._id || "");
    setFieldValue("vehicleVariant", selectedItem?.vehicleVariant || "");
    if (setShowCalculationResult) setShowCalculationResult(false);

    if (selectedItem) {
      // Who underwrites OD and TP follows the pick: a Make & Model row is one
      // company's (OD = TP), a Combined Make & Model is the OD insurer's row plus
      // the TP insurer's row. Both products are always generated. One writer sets
      // settings and every flat company field from that (see writeRouting).
      if (!selectedItem.company) {
        ToastError(
          "No company mapped for this Make & Model. Please update it in the Make & Model master."
        );
      }
      const { od, tp } = routingFor({ combo, row: selectedItem });
      writeRouting({ od, tp, pa: routing.PACompany });

      // Auto-fill the discount: the logged-in user's own value for this
      // make/model when they have one, otherwise the master's.
      setFieldValue("discount", resolveDiscount(selectedItem));

      // Set IDV range hint — derived from the Manufacturer's Selling Price
      // when the master has one, otherwise the stored range.
      const selectedRange = resolveIdvRange(selectedItem);
      setIdvRangeHint(`${selectedRange.from} to ${selectedRange.to}`);

      // Carry the "model not found" configuration over from the master.
      applyModelNotFound(selectedItem);

      if (
        selectedItem.modelNotFound &&
        !isNationalCompany(selectedItem.company)
      ) {
        // Reliance only: the portal computes the IDV from the selling price, so
        // our IDV field is not used — applyModelNotFound stored 0 and the range
        // auto-fill below is skipped. National falls through and is seeded with
        // the midpoint like any other vehicle, because its operator types it.
        setIdvError("");
      } else if (selectedRange.from || selectedRange.to) {
        // Auto-fill IDV with the MIDDLE of the range, not its floor — 100 to
        // 200 gives 150. With a selling price on the master that range is
        // 90%–99% of it, so the IDV traces straight back to the price entered.
        setFieldValue("idv", midpointIdv(selectedRange.from, selectedRange.to));
        setIdvError("");
      } else if (values.idv) {
        // No range on the record — validate whatever is already entered
        validateIDV(values.idv, selectedItem);
      }
    } else {
      writeRouting({ od: "", tp: "", pa: "" });
      setFieldValue("discount", "");
      setIdvRangeHint("");
      setIdvError("");
    }
  };

  // Handle IDV value change with validation
  const handleIDVChange = (e) => {
    const value = e.target.value;
    setFieldValue("idv", value);
    if (setShowCalculationResult) setShowCalculationResult(false);

    // Validate IDV against selected make-model range
    if (selectedMakeModel) {
      validateIDV(value, selectedMakeModel, true);
    }
  };

  /**
   * Open the full Motor Calculation page in a NEW TAB with these figures.
   *
   * The breakup shown here and that page's calculator run the SAME engine
   * (calcCarAndBike), and its form is driven by an `inputData` object whose
   * keys come from its field labels — "IDV" -> iDV, "CC" -> cC, "Year Of
   * Manufacture" -> yearOfManufacture. utils/premiumCalculator.js already
   * builds exactly that shape to run the policy's own quote, so the same
   * mapping is reproduced here rather than invented.
   *
   * A new tab cannot be handed React Router state, so the payload travels
   * through localStorage under a one-shot token — see utils/prefillHandoff.
   */
  const handleOpenInCalculator = () => {
    // The CC own damage was priced on (premiumInputs.parseCc has already read the
    // master's text), so that page picks the same rate band this policy did.
    const cc = Number.isFinite(Number(calculationResult?.odCc))
      ? Number(calculationResult.odCc)
      : selectedMakeModel?.CC || selectedMakeModel?.cc || "";
    // Multiple company: third party is priced on the TP insurer's OWN vehicle,
    // and that page has one CC box. The CC the TP premium was priced on travels
    // beside the form values — see prefillHandoff.tpCcOverride.
    const tpCc = Number(calculationResult?.tpCc);
    const discount = parseFloat(values?.discount);
    const zeroDepRaw = parseFloat(values?.zeroDepreciationPercentage);

    const token = stashPrefill({
      // Which vehicle tile the page should open. 2 = New Two Wheeler Vehicle,
      // the only form this policy screen quotes.
      formId: 2,
      inputData: {
        policyType: "Full",
        iDV: values?.idv || "",
        yearOfManufacture: values?.manufacturingYear || "",
        cC: cc,
        zone: values?.zone === "B" ? "B" : "A",
        ...(Number.isFinite(discount) && discount > 0
          ? { ["discountonODPremium(%)"]: discount }
          : {}),
        ...(values?.zeroDepreciation && Number.isFinite(zeroDepRaw)
          ? { zeroDepreciation: zeroDepRaw }
          : {}),
        ...(values?.tppdRestrict ? { tPPDRestrict: "yes" } : {}),
        // Matches premiumCalculator.js: PA Cover's amount IS the PA to Owner
        // Driver figure, so it flows through Total B / GST like any other TP
        // line on the calculator page too.
        ...(values?.paCover
          ? { pAOwnerDriver: Number(values?.paCoverAmount) || 0 }
          : {}),
      },
      ...(mode === "multiple" && Number.isFinite(tpCc) && tpCc !== Number(cc) ? { tpCc } : {}),
      // Shown on the page so it is obvious which policy the figures came from.
      label: selectedMakeModel?.label || "",
    });

    if (!token) {
      // A tab that opens on an empty calculator is worse than no tab.
      ToastError(
        "Could not open the calculator — this browser is blocking local storage."
      );
      return;
    }

    // NO "noopener" here, deliberately.
    //
    // The app authenticates from sessionStorage ("token", read by PrivateRoute
    // in Routing/Main.js), and a browser only copies sessionStorage into a new
    // tab when that tab is created FROM this one — i.e. when it has an opener.
    // "noopener" severs exactly that link, so the tab opened with an empty
    // sessionStorage, found no token, and bounced straight to the login page.
    //
    // Nothing is at risk: this is our own origin, and the value of noopener is
    // keeping an untrusted page away from window.opener.
    window.open(prefillUrl(token, "/motorCalculation"), "_blank");
  };

  // PA Cover's amount is now folded into the engine's own PA-to-Owner-Driver
  // line (see utils/premiumCalculator.js), so calculationResult.finalPremium
  // ALREADY includes it, taxed together with OD+TP at the same 18% GST. GST
  // is linear, so backing it out here (finalPremium - cpaWithGst) reproduces
  // the same "Motor Premium" the old, un-folded calculation showed.
  const cpaWithGst = values.paCover
    ? (Number(values?.paCoverAmount) || 0) * 1.18
    : 0;
  // OD only: PA is not inside the result, so it is added (not backed out).
  const resultPremium = Number(calculationResult?.finalPremium) || 0;
  const finalPremiumWithPaCover = coverage === "od" ? resultPremium + cpaWithGst : resultPremium;
  const motorOnlyPremium = finalPremiumWithPaCover - cpaWithGst;

  // Per-company split (Reliance OD / National TP ...). Same numbers the wallet
  // debits — see companyRules.premiumSplit. Only shown when isSplit; a
  // single-company policy keeps the familiar Motor / CPA / Total block.
  const premiumSplitView = splitFromCalculation(calculationResult, values, {
    multiProduct: multiProductPolicy,
    chargeBasis: chargeBasisOf(values),
  });
  // Multiple company: the figures the Motor Calculation page ends on — Total A
  // is the OD insurer's side, Total B the TP insurer's — straight from the engine
  // result, so the panels, the breakup dialog and that page show the same numbers.
  // Null for a single company, and while there is no current calculation: a
  // pricing field that changes hides the totals until Calculate is pressed again.
  const totals = (showSidePanels || showPartPanel) && showCalculationResult ? premiumTotals(calculationResult) : null;
  // OD only: PA never went into the engine (it sits in Total B, which this policy does not have), so it is its own
  // amount ON TOP of Net / GST / Final. Every other cover already has PA inside Total B and the engine's figures stand.
  const odPaNet = coverage === "od" && values.paCover ? Number(values.paCoverAmount) || 0 : 0;
  const footer = totals
    ? odPaNet > 0
      ? {
          pa: odPaNet,
          net: totals.net === null ? null : Number((totals.net + odPaNet).toFixed(2)),
          gst: totals.gst === null ? null : Number((totals.gst + odPaNet * 0.18).toFixed(2)),
          final: totals.final === null ? null : Number((totals.final + odPaNet * 1.18).toFixed(2)),
        }
      : { pa: totals.pa, net: totals.net, gst: totals.gst, final: totals.final }
    : null;

  // The saved premium is standing in for a calculation (edit, nothing repriced).
  const savedPremiumInUse =
    !!showCalculationResult && !!calculationResult?._savedPolicyPremium;

  // Where the master now differs from what this edited policy was saved with.
  const driftAll = computeMasterDrift({
    snapshot: savedSnapshot,
    makeModelId: values.makeModel,
    rtoCode: values.rtoCityLocation,
    master: selectedMakeModel,
    masterDiscount: resolveDiscount(selectedMakeModel),
    masterPaMin: soldWithPaCompany(routing.PACompany) ? masterPaFloor : undefined,
    combo: mode === "multiple" ? selectedCombo : null,
    currentTpRow:
      mode === "multiple" && selectedCombo
        ? comboOptions.find((o) => String(o._id) === String(selectedCombo._id))?.tpRow || null
        : null,
    rto: rtoValue
      ? {
          region: rtoValue.region,
          city: rtoValue.city,
          zone: rtoZone ? zoneKeyFromName(rtoZone.zoneName) : null,
        }
      : null,
  });
  // A selling price typed for this policy is not "the master changed" - the blue note below says what it is.
  const driftItems = values.idvByHand ? driftAll.filter((i) => i.key !== "sellingPrice") : driftAll;

  // ── Selling price -> IDV (the IDV & Selling Price section and the IDV Calculator; utils/idvByHand.js) ──
  // The IDV range is always the Make & Model's (the owner: it is never edited on a policy); only the price and the IDV
  // are this policy's own.
  const idvRangeNow = idvRangeFor(selectedMakeModel);
  const idvHandRows = values.idvByHand ? byHandChanges({ record: selectedMakeModel, values }) : [];

  /** Update in the IDV Calculator: the price and the IDV go on this policy; the premium has to be worked out again. */
  const applyIdvCalculator = (calc, { quiet = false } = {}) => {
    const next = appliedValues(calc, selectedMakeModel);
    setFieldValue("manufacturerSellingPrice", next.manufacturerSellingPrice);
    setFieldValue("idvByHand", next.idvByHand);
    setFieldValue("idv", next.idv);
    validateIDV(next.idv, selectedMakeModel, true); // the calculator only lets an IDV inside the range through
    if (setShowCalculationResult) setShowCalculationResult(false);
    setTimeout(() => formik?.validateForm?.(), 0);
    setIdvCalcOpen(false);
    if (!quiet) ToastSuccess("IDV updated — recalculate the premium.");
  };

  /** "Use master values" in the blue note: the Make & Model's selling price and the middle IDV of its range again. */
  const restoreMasterIdv = () => {
    applyIdvCalculator(masterCalc(selectedMakeModel, idvRangeNow), { quiet: true });
    ToastSuccess("Master selling price applied — recalculate the premium.");
  };

  /**
   * OD + TP / OD only / TP only (Single company). Changes which side of the premium exists, so the result is cleared
   * and recalculated. TP only drops the own-damage inputs (discount, Zero Depreciation — whose default comes back with
   * OD + TP or OD only); OD only drops TPPD, a third-party item.
   */
  const handleCoverage = (next) => {
    const from = coverageOf(values);
    if (next === from) return;
    setFieldValue("coverage", next);
    setFieldValue("odSelected", next !== "tp");
    setFieldValue("tpSelected", next !== "od");
    if (next === "tp") {
      setFieldValue("zeroDepreciation", false);
      setFieldValue("zeroDepreciationPercentage", "");
      setFieldValue("discount", "");
    } else if (from === "tp") {
      setFieldValue("zeroDepreciation", true); // the form's default
    }
    if (next === "od") setFieldValue("tppdRestrict", false);
    if (setShowCalculationResult) setShowCalculationResult(false);
    setTimeout(() => formik?.validateForm?.(), 0);
  };

  /**
   * The tab the row of four shows: Single company, Multiple company, OD only or TP only. OD only / TP only are Single
   * company (one insurer, the vehicle row picks it) with a cover of "od" / "tp".
   */
  const tab = mode === "multiple" ? "multiple" : coverage === "od" ? "od" : coverage === "tp" ? "tp" : "single";
  const handleTab = (next) => {
    if (next === tab) return;
    if (next === "multiple") return handleCompanyChoice("multiple"); // also puts the cover back to OD + TP
    // The other three are one insurer: leave Multiple first (that clears the combined vehicle), then set the cover.
    if (mode === "multiple") handleCompanyChoice("single");
    // OD only is Reliance only: a vehicle of another insurer picked before is cleared (pick a Reliance one).
    else if (next === "od" && selectedMakeModel && !isReliance(selectedMakeModel.company)) handleMakeModelChange(null);
    handleCoverage(next === "single" ? "both" : next);
  };

  /**
   * Single <-> Multiple company. The vehicle picked under the other mode no longer
   * applies (a row of one company vs a record of two), so the pick, the companies
   * and the premium are cleared and the user chooses again.
   */
  const handleCompanyChoice = (next) => {
    if (next === mode) return;
    handleMakeModelChange(null); // releases the saved vehicle, clears the pick, the routing and the result
    setFieldValue("companyChoice", next);
    if (next === "multiple") handleCoverage("both"); // two insurers always sell OD + TP
    setFieldValue("makeModelCombo", "");
    setFieldValue("_legacyMultiple", false);
  };

  /**
   * "Use latest master values": stop keeping the saved vehicle and RTO values,
   * take the master's, and make the premium be recalculated. The saved insurers
   * are deliberately left alone (re-pick the vehicle to re-route).
   */
  const applyLatestMaster = () => {
    releaseSaved?.("vehicle", "rto");
    setFieldValue("idvByHand", false); // the master's selling price again
    // A saved combined policy holds its record as rebuilt from the policy (the TP
    // vehicle it was sold with). "Latest" means the record as the master has it now,
    // so the premium is repriced on the TP row's current CC — the same CC the server
    // will store when it rebuilds the TP vehicle.
    if (mode === "multiple" && selectedCombo) {
      const current = comboOptions.find((o) => String(o._id) === String(selectedCombo._id));
      if (current && current.odRow) {
        setSelectedCombo?.(current);
        setSelectedMakeModel?.(current.odRow);
      }
    }
    if (selectedMakeModel?._id && !selectedMakeModel._missingFromMaster) {
      refreshFromVehicleMaster(selectedMakeModel, { reroute: false, quiet: true });
    }
    if (setShowCalculationResult) setShowCalculationResult(false);
    ToastSuccess("Latest master values applied — recalculate the premium.");
  };

  // ── Fields that belong to one SIDE of the premium ─────────────────────────
  // IDV, discount and Zero Depreciation price OWN DAMAGE; TPPD prices THIRD PARTY.
  // They are defined once here: Single company lays them out in the usual grid,
  // Multiple company puts the first group in the OD panel and TPPD in the TP panel.
  const tppdOffered = tppdAvailable || (keepVehicle && !!savedSnapshot?.tppdRestrict);

  // Single company has the selling price and the IDV in a section of their own below "Policy & vehicle" (the owner,
  // 2026-10-27) with the IDV Calculator in its header; OD only / Multiple company keep them in the OD panel, the
  // calculator beside the IDV label.
  const idvInOwnSection = mode !== "multiple" && !showPartPanel;

  // Not while Reliance's portal works the IDV out itself (model not in its list)
  const idvCalculatorButton = idvLockedByPortal ? null : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CalculateOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                    onClick={() => setIdvCalcOpen(true)}
                    data-testid="open-idv-calculator"
                    sx={{
                      ...TOOL_BUTTON_SX,
                      fontSize: "12px",
                      py: 0.25,
                      px: 1.25,
                      minWidth: 0,
                      lineHeight: 1.6,
                      borderRadius: "7px",
                      flexShrink: 0,
                    }}
                  >
                    IDV Calculator
                  </Button>
  );

  const idvField = (
    <>
              {/* In a row with the calculator button, so the form's "label right above an input" rule
                  (onlinePolicyForm.scss) no longer reaches it: the same look is set here. */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: "6px" }}>
                <Typography sx={FIELD_LABEL_SX}>
                  IDV (Insured Declared Value){idvLockedByPortal ? "" : " *"}
                </Typography>
                {idvInOwnSection ? null : idvCalculatorButton}
              </Box>
              <Field
                name="idv"
                placeholder={
                  idvLockedByPortal
                    ? "Calculated by the insurer from the selling price"
                    : idvRangeHint
                      ? `Enter IDV (Range: ${idvRangeHint})`
                      : "IDV (Insured Declared Value)"
                }
                type="number"
                className="textField w-100"
                disabled={idvLockedByPortal}
                onChange={handleIDVChange}
              />
              {values.modelNotFound && (
                <Typography sx={{ fontSize: "12px", color: "#2e7d32", mt: 0.5, fontWeight: 600 }}>
                  Model not in the insurer's list — IDV{" "}
                  {idvLockedByPortal ? "is derived from" : "range comes from"}{" "}
                  the Manufacturer's Selling Price
                  {values.manufacturerSellingPrice
                    ? ` (₹${Number(values.manufacturerSellingPrice).toLocaleString()})`
                    : ""}
                  .
                </Typography>
              )}
              {/* One line, not two (the owner): the red range error below already names the range */}
              {idvRangeHint && !idvError && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#475467",
                    mt: 0.5,
                    // `fontStyle: "bold"` used to sit here — not a valid value
                    // for that property, so it never did anything. fontWeight
                    // below is what actually renders this bold.
                    fontWeight: 600,
                  }}
                >
                  Allowed range (Make &amp; Model): {idvRangeHint}
                </Typography>
              )}
              <div className="errorMessage mb-2 mt-2">
                {idvError ? (
                  <div style={{ color: "#d32f2f" }}>{idvError}</div>
                ) : errors.idv && touched.idv ? (
                  <div>{errors.idv}</div>
                ) : (
                  " "
                )}
              </div>
    </>
  );

  /**
   * The Manufacturer Selling Price, shown and editable (the owner, 2026-10-27): the master's by default; a price typed
   * here gives the IDV (price less the vehicle's depreciation) and the IDV box stays editable. The range is the Make &
   * Model's and is not touched: an IDV outside it shows the usual error. A price that differs from the master makes the
   * policy "set by hand" (blue note; the master is not changed).
   */
  const handleSellingPriceChange = (event) => {
    const next = fromSellingPrice(event.target.value, { record: selectedMakeModel, keepIdv: idvLockedByPortal });
    Object.entries(next).forEach(([field, value]) => setFieldValue(field, value));
    if (next.idv) validateIDV(next.idv, selectedMakeModel, true);
    if (setShowCalculationResult) setShowCalculationResult(false);
  };

  const priceField = (
    <>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>Manufacturer Selling Price</Typography>
              <Field
                name="manufacturerSellingPrice"
                placeholder="Ex-showroom price"
                type="number"
                className="textField w-100"
                onChange={handleSellingPriceChange}
                data-testid="selling-price"
              />
              <Typography sx={{ fontSize: "12px", color: "#475467", mt: 0.5, fontWeight: 600 }}>
                {Number(values.manufacturerSellingPrice) > 0
                  ? idvLockedByPortal
                    ? "The insurer works the IDV out from this price."
                    : "The IDV is worked out from this price."
                  : "Type the price to work the IDV out."}
              </Typography>
              <div className="errorMessage mb-2 mt-2"> </div>
    </>
  );

  const discountField = (
    <>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Discount
              </Typography>
              <Field
                name="discount"
                placeholder="Discount"
                type="text"
                className="textField w-100"
                onChange={(e) => {
                  const inputValue = e.target.value || "";
                  let numericValue = inputValue
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1");
                  // Percentage cannot exceed 100
                  if (numericValue !== "" && Number(numericValue) > 100) {
                    numericValue = "100";
                  }
                  // Both the filter and the clamp can change the value, and
                  // writing a changed value back to a controlled input drops
                  // the caret to the end — keep it where the user left it.
                  preserveCaret(e.target, inputValue, numericValue);
                  setFieldValue("discount", numericValue);
                  if (setShowCalculationResult) setShowCalculationResult(false);
                }}
              />
              <div className="errorMessage mb-2 mt-2"> </div>
    </>
  );

  const zeroDepRow = (
    <>
              <Box sx={OPTION_LABEL_SX}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.zeroDepreciation}
                      onChange={async (e) => {
                        const isChecked = e.target.checked;
                        setFieldValue("zeroDepreciation", isChecked);
                        if (!isChecked) {
                          setFieldValue("zeroDepreciationPercentage", "");
                          // Prepare updated form values for recalculation
                          const updatedValues = {
                            ...values,
                            zeroDepreciation: isChecked,
                            zeroDepreciationPercentage: "",
                          };
                          // Trigger recalculation if handler is provided
                          if (onZeroDepreciationChange) {
                            await onZeroDepreciationChange(
                              isChecked,
                              updatedValues
                            );
                          }
                        }
                        if (setShowCalculationResult)
                          setShowCalculationResult(false);
                      }}
                    />
                  }
                  label="Zero Depreciation"
                />
              </Box>
              {values.zeroDepreciation && (
                <Box sx={{ width: OPTION_FIELD_WIDTH }}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                    Nil Dip Price *
                  </Typography>
                  <Field
                    name="zeroDepreciationPercentage"
                    placeholder="Nil Dip Price"
                    type="text"
                    className="textField w-100"
                    disabled={masterHasNilDip}
                    onChange={(e) => {
                      const raw = e.target.value || "";
                      const numericValue = raw
                        .replace(/[^0-9.]/g, "")
                        .replace(/(\..*)\./g, "$1");
                      preserveCaret(e.target, raw, numericValue);
                      setFieldValue("zeroDepreciationPercentage", numericValue);
                      if (setShowCalculationResult) setShowCalculationResult(false);
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: masterHasNilDip ? "#667085" : "#b54708",
                      mt: 0.5,
                    }}
                  >
                    {masterHasNilDip
                      ? keeps("zeroDepreciationPercentage") &&
                        !sameValue(values.zeroDepreciationPercentage, masterNilDip)
                        ? `Saved with this policy (the master now says ${masterNilDip}).`
                        : "From the Make & Model master."
                      : "Not set on the master — it will be saved there."}
                  </Typography>
                  <div className="errorMessage mb-2 mt-2">
                    {errors.zeroDepreciationPercentage &&
                      touched.zeroDepreciationPercentage ? (
                      <div>{errors.zeroDepreciationPercentage}</div>
                    ) : (
                      " "
                    )}
                  </div>
                </Box>
              )}
    </>
  );

  const tppdRow = (
                <Box sx={OPTION_LABEL_SX}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.tppdRestrict}
                        onChange={async (e) => {
                          const isChecked = e.target.checked;
                          setFieldValue("tppdRestrict", isChecked);
                          const updatedValues = {
                            ...values,
                            tppdRestrict: isChecked,
                          };
                          if (onTppdRestrictChange) {
                            await onTppdRestrictChange(isChecked, updatedValues);
                          }
                          if (setShowCalculationResult)
                            setShowCalculationResult(false);
                        }}
                      />
                    }
                    label="TPPD Restrict"
                  />
                </Box>
  );

  // The Coverage / PA Cover box (with its nominee fields). Drawn ONCE: on a wide screen with the OD / TP panels on
  // the right it fills the left column under the fields (it was empty there), otherwise in its own place below.
  const coverageBeside = wideForm && (showPartPanel || mode === "multiple");
  // Beside the panels it is a group inside the "Policy & vehicle" card; otherwise a card of its own under it.
  const CoverageFrame = coverageBeside ? FormSubsection : FormSection;
  const coverageSection = (
    <>
      {/* Coverage options — Zero Depreciation, TPPD and PA Cover.
          Every row uses OPTION_ROW_SX so the checkboxes and the fields beside
          them line up in two clean columns. */}
      <CoverageFrame
        title={mode === "multiple" || showPartPanel ? "PA Cover" : "Coverage Options"}
        description={
          mode === "multiple" || showPartPanel
            ? "Personal accident cover for the owner-driver, and its nominee."
            : "Add-on covers and the owner-driver's personal accident cover."
        }
        testId="section-coverage"
      >
          <Box
            // The options, one under the other with a thin rule between them. A plain block, not a wrapping flex
            // column: that measured each row before its width was known and stretched the PA row (an empty gap).
            className="op-options-card"
            sx={{ "& > * + *": { borderTop: "1px solid #F2F4F7", pt: 2, mt: 1 } }}
          >
            {mode !== "multiple" && !showPartPanel && (
            <Grid item xs={12} sx={OPTION_ROW_SX}>
              {zeroDepRow}
            </Grid>
            )}

            {/* TPPD Restrict is offered per VEHICLE: the Make & Model master's
                switch decides. When it is off the checkbox is hidden and the
                effect above forces tppdRestrict false, so a hidden field can
                never quietly change the premium. */}
            {mode !== "multiple" && !showPartPanel && tppdOffered ? (
              <Grid item xs={12} sx={OPTION_ROW_SX}>
                {tppdRow}
              </Grid>
            ) : null}

            <Grid item xs={12} sx={OPTION_ROW_SX}>
              <Box sx={OPTION_LABEL_SX}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.paCover}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setFieldValue("paCover", isChecked);
                        // Clear the dependent values when switching PA Cover off,
                        // otherwise the amount lingers and keeps showing in the
                        // premium summary — and gets added to the total.
                        if (!isChecked) {
                          setFieldValue("paCoverAmount", "");
                          // PA off: settings keeps the Brisk default for when it
                          // is switched back on; the flat fields are cleared as
                          // they always were.
                          writeRouting({
                            od: routing.ODCompany,
                            tp: routing.TPCompany,
                            pa: defaultPaFor(routing) || BRISK,
                            paCover: false,
                          });
                          setFieldValue("paCoverCompany", "");
                          if (formik && formik.setFieldTouched) {
                            formik.setFieldTouched("notPaCoverReason", true);
                          }
                        } else {
                          // Switching on starts from Brisk, the long-standing default
                          // (the first offered insurer when Brisk is switched off).
                          const paCompany = defaultPaFor(routing);
                          writeRouting({
                            od: routing.ODCompany,
                            tp: routing.TPCompany,
                            pa: paCompany,
                            paCover: true,
                          });
                          setFieldValue("_paCoverMinAmount", paFloorForCompany(paCompany));
                          fillBriskPaMinimum(paCompany);
                          setFieldValue("notPaCoverReason", "");
                        }
                        // PA Cover changes the premium (premiumCalculator adds
                        // pAOwnerDriver for it), so the figure on screen is no
                        // longer valid — drop it and show Calculate again.
                        if (setShowCalculationResult) setShowCalculationResult(false);
                      }}
                    />
                  }
                  label="PA Cover"
                />
              </Box>
              {values.paCover && (
                <Box sx={{ width: OPTION_FIELD_WIDTH }}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                    PA Cover Company *
                  </Typography>
                  <Autocomplete
                    className="AutoComplete_InputBox"
                    sx={{ width: "100%" }}
                    disablePortal
                    disableClearable
                    value={
                      paCompanyChoices.find((c) => sameCompany(c, routing.PACompany)) ||
                      null
                    }
                    options={paCompanyAll}
                    getOptionDisabled={(option) =>
                      paCompanyLocked.some((c) => sameCompany(c, option))
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option}>
                        {option}
                        {paCompanyLocked.some((c) => sameCompany(c, option)) && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: "#98A2B3" }}>
                            (needs multi-company mode)
                          </span>
                        )}
                      </li>
                    )}
                    onChange={(e, v) => {
                      // settings.PACompany is the choice; paCoverCompany
                      // (Brisk | Company) and paCoverInsurerCompany are derived.
                      writeRouting({
                        od: routing.ODCompany,
                        tp: routing.TPCompany,
                        pa: v || "",
                      });
                      setFieldValue("_paCoverMinAmount", paFloorForCompany(v));
                      movePaAmountToMinimum(v);
                      setFieldValue("notPaCoverReason", ""); // Reset the reason field when company changes
                      if (setShowCalculationResult) setShowCalculationResult(false);
                    }}
                    getOptionLabel={(option) => option || ""}
                    isOptionEqualToValue={(option, value) => sameCompany(option, value)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Brisk or an insurer" />
                    )}
                  />
                  <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>
                    {isBriskPaCover
                      ? "A Brisk CPA/RSA certificate will be created."
                      : `Bought with the ${routing.PACompany || "insurer"} policy — no Brisk certificate is created.`}
                  </Typography>
                  {paCompanyLocked.length > 0 && (
                    <Typography sx={{ fontSize: 12, color: "#B54708", mt: 0.5 }}>
                      {`${paCompanyLocked.join(", ")} can't provide PA yet: this policy runs as a single ${routing.ODCompany} job. It becomes available when multi-company mode is switched on.`}
                    </Typography>
                  )}
                  <div className="errorMessage mb-2 mt-2">
                    {errors.paCoverCompany && touched.paCoverCompany ? (
                      <div>{errors.paCoverCompany}</div>
                    ) : (
                      " "
                    )}
                  </div>
                </Box>
              )}
              {values.paCover && (
                <Box sx={{ width: OPTION_FIELD_WIDTH }}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                    PA Cover Amount *
                  </Typography>
                  <Field
                    name="paCoverAmount"
                    as={TextField}
                    placeholder={"Enter the PA amount"}
                    type="number"
                    min={paCoverMinAmount}
                    className="textField w-100"
                    // The PA amount IS part of the premium, so editing it must
                    // invalidate the calculated figure. Without an onChange the
                    // Field used Formik's default handler, which left a stale
                    // premium on screen and no Calculate button.
                    onChange={(e) => {
                      setFieldValue("paCoverAmount", e.target.value);
                      if (setShowCalculationResult) setShowCalculationResult(false);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                  <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>
                    {paFloorLoweredBySaved
                      ? `Minimum ₹${paCoverMinAmount} — the amount saved with this policy (the master now requires ₹${masterPaFloor}).`
                      : paCoverMinAmount === (isBriskPaCover ? BRISK_PA_COVER_MIN : DEFAULT_PA_COVER_MIN)
                        ? `Minimum ₹${paCoverMinAmount} (default).`
                        : `Minimum ₹${paCoverMinAmount} for this vehicle.`}
                  </Typography>
                  <div className="errorMessage mb-2 mt-2">
                    {errors.paCoverAmount && touched.paCoverAmount ? (
                      <div>{errors.paCoverAmount}</div>
                    ) : (
                      " "
                    )}
                  </div>
                </Box>
              )}
              {hasCompanyLeg(values, isKshema) && !(values.paCover && sameCompany(routing.PACompany, "KSHEMA")) && (
                <Box sx={{ width: OPTION_FIELD_WIDTH + 150 }}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                    Reason For Unselecting The company *
                  </Typography>
                  <Autocomplete
                    name="notPaCoverReason"
                    className="AutoComplete_InputBox"
                    disablePortal
                    options={[
                      { label: "Already have personal accident policy With 15L Sum Insured", value: "Already have personal accident policy With 15L Sum Insured" },
                      { label: "I already have a personal accident policy for my other vehicle.", value: "I already have a personal accident policy for my other vehicle." },
                      { label: "No Valid and Effective DL", value: "No Valid and Effective DL" }
                    ]}
                    value={values.notPaCoverReason ? { label: values.notPaCoverReason, value: values.notPaCoverReason } : null}
                    onChange={(e, v) => setFieldValue("notPaCoverReason", v?.value || "")}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => <TextField {...params} placeholder="Select Reason" />}
                  />
                  <div className="errorMessage mb-2 mt-2">
                    {errors.notPaCoverReason && touched.notPaCoverReason ? (
                      <div>{errors.notPaCoverReason}</div>
                    ) : (
                      " "
                    )}
                  </div>
                </Box>
              )}
            </Grid>
            {/* National asks for a nominee on every quote, PA Cover or not.
                Kept separate from PA Cover Details below — that section is
                PA-cover-specific (years, appointee, etc.) and only National
                needs a nominee without PA Cover being on at all. */}
            {hasNationalLeg && !values.paCover && (
              <>
                <Grid item xs={12}>
                  <Grid container sx={{ mb: 0.5 }} columnSpacing={3}>
                    <Grid item xs={12}>
                      <Typography sx={{ ...SUBHEAD_SX, mb: 0.5 }}>
                        Nominee Details
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#667085", mb: 2 }}>
                        National requires a nominee on every quote. Auto-filled from
                        your saved default below — change it here if this policy
                        needs a different one.
                      </Typography>
                      {nationalNomineeDefault && !nationalNomineeDefault.name && !values.nomineeName && (
                        <Typography sx={{ fontSize: 12, color: "#B42318", mb: 2, fontWeight: 600 }}>
                          No default nominee is saved for National. Go to Profile →
                          Account & Policy Settings → National → Nominee Details to
                          save one, or fill it in below for just this policy.
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                        Nominee Name *
                      </Typography>
                      <Field
                        name="nomineeName"
                        placeholder="Nominee Name"
                        className="textField w-100"
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^a-zA-Z\s]/g, "")
                            .toUpperCase();
                          setFieldValue("nomineeName", value);
                        }}
                      />
                      <div className="errorMessage mb-2 mt-2">
                        {errors.nomineeName && touched.nomineeName ? (
                          <div>{errors.nomineeName}</div>
                        ) : (
                          " "
                        )}
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                        Nominee Relation *
                      </Typography>
                      <Autocomplete
                        name="nomineeRelation"
                        className="AutoComplete_InputBox"
                        disablePortal
                        value={nomineeValue}
                        options={nominee.relations}
                        onChange={(e, v) => {
                          setFieldValue("nomineeRelation", v?.value || "");
                        }}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Nominee Relation" />
                        )}
                      />
                      <div className="errorMessage mb-2 mt-2">
                        {errors.nomineeRelation && touched.nomineeRelation ? (
                          <div>{errors.nomineeRelation}</div>
                        ) : (
                          " "
                        )}
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                        Nominee Age *
                      </Typography>
                      <Field
                        name="nomineeAge"
                        type="number"
                        placeholder="Nominee Age"
                        className="textField w-100"
                      />
                      <div className="errorMessage mb-2 mt-1">
                        {errors.nomineeAge && touched.nomineeAge ? (
                          <div>{errors.nomineeAge}</div>
                        ) : (
                          " "
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
            {values.paCover && (
              <>
                <Grid item xs={12}>
                  <Grid container sx={{ mb: 0.5 }} columnSpacing={3}>
                    <Grid item xs={12}>
                      <Typography sx={{ ...SUBHEAD_SX, mb: 1.5 }}>
                        PA Cover Details
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                        Nominee Name *
                      </Typography>
                      <Field
                        name="nomineeName"
                        placeholder="Nominee Name"
                        className="textField w-100"
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^a-zA-Z\s]/g, "")
                            .toUpperCase();
                          setFieldValue("nomineeName", value);
                        }}
                      />
                      <div className="errorMessage mb-2 mt-2">
                        {errors.nomineeName && touched.nomineeName ? (
                          <div>{errors.nomineeName}</div>
                        ) : (
                          " "
                        )}
                      </div>
                    </Grid>

                    {nominee.type && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                          Nominee Type *
                        </Typography>
                        <Autocomplete
                          name="nomineeType"
                          className="AutoComplete_InputBox"
                          disablePortal
                          disableClearable
                          value={nomineeTypeValue}
                          options={nomineeTypeOptions}
                          onChange={(e, v) => {
                            setFieldValue("nomineeType", v?.value || "");
                          }}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) => option?.value === value?.value}
                          renderInput={(params) => <TextField {...params} placeholder="Nominee Type" />}
                        />
                        <div className="errorMessage mb-2 mt-2">
                          {errors.nomineeType && touched.nomineeType ? (
                            <div>{errors.nomineeType}</div>
                          ) : (
                            " "
                          )}
                        </div>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4}>
                      <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                        Nominee Relation *
                      </Typography>
                      <Autocomplete
                        name="nomineeRelation"
                        className="AutoComplete_InputBox"
                        disablePortal
                        value={nomineeValue}
                        options={nominee.relations}
                        onChange={(e, v) => {
                          setFieldValue("nomineeRelation", v?.value || "");
                        }}
                        getOptionLabel={(option) => {
                          if (!option) return "";
                          return option.label || "";
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Nominee Relation"
                          />
                        )}
                      />
                      <div className="errorMessage mb-2 mt-2">
                        {errors.nomineeRelation && touched.nomineeRelation ? (
                          <div>{errors.nomineeRelation}</div>
                        ) : (
                          " "
                        )}
                      </div>
                    </Grid>

                    {nominee.share && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                          Nominee Share (%) *
                        </Typography>
                        <Field
                          name="nomineeShare"
                          placeholder="Nominee Share"
                          className="textField w-100"
                          onChange={(e) => {
                            // Digits only, capped at 100 — the portal rejects
                            // anything else and a stray decimal is far easier
                            // to block here than to explain after a failure.
                            let v = (e.target.value || "").replace(/[^0-9]/g, "");
                            if (v !== "" && Number(v) > 100) v = "100";
                            setFieldValue("nomineeShare", v);
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                        <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>
                          This form captures one nominee, so this is normally 100.
                        </Typography>
                        <div className="errorMessage mb-2 mt-1">
                          {errors.nomineeShare && touched.nomineeShare ? (
                            <div>{errors.nomineeShare}</div>
                          ) : (
                            " "
                          )}
                        </div>
                      </Grid>
                    )}

                    {nominee.gender && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                          Nominee Gender *
                        </Typography>
                        <Autocomplete
                          name="nomineeGender"
                          className="AutoComplete_InputBox"
                          disablePortal
                          value={nomineeGenderValue}
                          options={nominee.genders}
                          onChange={(e, v) => {
                            setFieldValue("nomineeGender", v?.value || "");
                          }}
                          getOptionLabel={(option) => option.label || ""}
                          isOptionEqualToValue={(option, value) => option?.value === value?.value}
                          renderInput={(params) => <TextField {...params} placeholder="Nominee Gender" />}
                        />
                        <div className="errorMessage mb-2 mt-1">
                          {errors.nomineeGender && touched.nomineeGender ? <div>{errors.nomineeGender}</div> : " "}
                        </div>
                      </Grid>
                    )}

                    {nominee.dob && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                            Nominee Date of Birth *
                          </Typography>
                          <DatePicker
                            name="nomineeDob"
                            className="Date_Picker w-100"
                            value={values.nomineeDob ? moment(values.nomineeDob) : null}
                            onChange={(date) => setFieldValue("nomineeDob", date ? date.toDate() : null)}
                            format="DD/MM/YYYY"
                            popupStyle={{ zIndex: 9999 }}
                          />
                          <div className="errorMessage mb-2 mt-1">
                            {errors.nomineeDob && touched.nomineeDob ? <div>{errors.nomineeDob}</div> : " "}
                          </div>
                        </Grid>
                      </>
                    )}

                    {nominee.years && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                            No of Year *
                          </Typography>
                          <Autocomplete
                            name="paCoverYears"
                            className="AutoComplete_InputBox"
                            disablePortal
                            value={
                              nationalTpYears
                                ? // a National TP has no default: empty until picked (or until a saved policy's term)
                                  (values._paYearsChosen && NATIONAL_PA_YEARS.find((o) => o.value === values.paCoverYears)) || null
                                : values.paCoverYears ? { label: `${values.paCoverYears} Year${values.paCoverYears > 1 ? 's' : ''}`, value: values.paCoverYears } : { label: "1 Year", value: 1 }
                            }
                            options={isKshema(nomineeCompany) ? [
                              { label: "1 Year", value: 1 },
                              { label: "2 Years", value: 2 },
                              { label: "3 Years", value: 3 },
                              { label: "4 Years", value: 4 },
                              { label: "5 Years", value: 5 },
                            ] : [
                              { label: "1 Year", value: 1 },
                              { label: "5 Years", value: 5 },
                            ]}
                            onChange={(e, v) => {
                              if (nationalTpYears) {
                                setFieldValue("paCoverYears", v ? v.value : "");
                                setFieldValue("_paYearsChosen", !!v);
                              } else {
                                setFieldValue("paCoverYears", v?.value || 1);
                              }
                            }}
                            getOptionLabel={(option) => option.label || ""}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => <TextField {...params} placeholder="Select Years" />}
                          />
                          {nationalTpYears && (
                            <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>
                              National's portal gets exactly this year.
                            </Typography>
                          )}
                          <div className="errorMessage mb-2 mt-1">
                            {errors.paCoverYears && touched.paCoverYears ? <div>{errors.paCoverYears}</div> : " "}
                          </div>
                        </Grid>
                      </>
                    )}

                    {hasNationalLeg && hasNomineeAge("National") && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                          Nominee Age *
                        </Typography>
                        <Field
                          name="nomineeAge"
                          type="number"
                          placeholder="Nominee Age"
                          className="textField w-100"
                        />
                        <div className="errorMessage mb-2 mt-1">
                          {errors.nomineeAge && touched.nomineeAge ? <div>{errors.nomineeAge}</div> : " "}
                        </div>
                      </Grid>
                    )}

                    {nominee.otherRelation &&
                      isOtherRelation(values.nomineeRelation) && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                            Specify Other Relation *
                          </Typography>
                          <Field
                            name="otherRelationName"
                            placeholder="Specify Relation"
                            className="textField w-100"
                            onChange={(e) => {
                              const value = e.target.value
                                .replace(/[^a-zA-Z\s]/g, "")
                                .toUpperCase();
                              setFieldValue("otherRelationName", value);
                            }}
                          />
                          <div className="errorMessage mb-2 mt-1">
                            {errors.otherRelationName && touched.otherRelationName ? (
                              <div>{errors.otherRelationName}</div>
                            ) : (
                              " "
                            )}
                          </div>
                        </Grid>
                      )}

                    {nominee.appointee && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                          Appointee Name *
                        </Typography>
                        <Field
                          name="appointeeName"
                          placeholder="Appointee Name"
                          className="textField w-100"
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^a-zA-Z\s]/g, "")
                              .toUpperCase();
                            setFieldValue("appointeeName", value);
                          }}
                        />
                        <div className="errorMessage mb-2 mt-1">
                          {errors.appointeeName && touched.appointeeName ? (
                            <div>{errors.appointeeName}</div>
                          ) : (
                            " "
                          )}
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
      </CoverageFrame>
    </>
  );

  return (
    <>
      <MasterDriftNotice
        items={driftItems}
        onApply={applyLatestMaster}
        premiumKept={savedPremiumInUse}
      />
      {idvHandRows.length > 0 && (
        <Box sx={{ px: "5px", pt: 2 }} data-testid="idv-by-hand-note">
          <Alert severity="info" sx={{ alignItems: "flex-start" }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              Selling price set by hand for this policy — the Make &amp; Model master is not changed
            </AlertTitle>
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {idvHandRows.map((row) => (
                <Box component="li" key={row.key} sx={{ mb: 0.5, fontSize: 13 }}>
                  <b>{row.label}:</b> {row.master} (master) → {row.policy}
                </Box>
              ))}
              {Number(values.idv) > 0 && (
                <Box component="li" sx={{ mb: 0.5, fontSize: 13 }}>
                  <b>IDV:</b> {rupees(values.idv)}
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: 12, color: "#475467", mt: 1 }}>
              The IDV must stay inside the Make &amp; Model range
              {idvRangeNow.from && idvRangeNow.to ? ` (${rupees(idvRangeNow.from)} to ${rupees(idvRangeNow.to)})` : ""}.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
              <Button size="small" variant="outlined" onClick={() => setIdvCalcOpen(true)} sx={{ textTransform: "none" }}>
                Open IDV Calculator
              </Button>
              <Button size="small" onClick={restoreMasterIdv} disabled={!selectedMakeModel} sx={{ textTransform: "none" }} data-testid="idv-use-master">
                Use master values
              </Button>
            </Box>
          </Alert>
        </Box>
      )}
      <PolicyIdvCalculatorDialog
        open={idvCalcOpen}
        onClose={() => setIdvCalcOpen(false)}
        record={selectedMakeModel}
        values={values}
        range={idvRangeNow}
        onApply={applyIdvCalculator}
      />
      <FormSection
        title="Policy & vehicle"
        description="The insurer, the vehicle and how it is priced."
        testId="section-policy-vehicle"
      >
          <Grid container className="op-fields-grid" columnSpacing={3}>
            {/* Company is auto-selected from the Make & Model master and hidden from the UI */}

            {/* ONE row of tabs: Single company | Multiple company | OD only | TP only.
                Single and Multiple are how many insurers; OD only / TP only are ONE insurer selling only that
                product (the company is then picked below, from the Make & Model, exactly like Single company).
                Multiple needs the server's multi-company switch (a saved combined policy still opens in it); OD
                only / TP only need its cover switch (a saved one still opens on its tab). */}
            {(multiProductPolicy || mode === "multiple" || coverChoice || coverage !== "both") && (
              <Grid item xs={12} className="op-span-all">
                {/* The owner picked the "pill track" from three mockups (2026-10-27): a grey track, the chosen one a
                    white raised pill; label above, the meaning of the choice under it. */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, mb: 2.5 }}>
                  <Typography sx={FIELD_LABEL_SX}>Company *</Typography>
                  {/* A one-of-four switch, so the current choice is unmistakable. */}
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={tab}
                    aria-label="Single company, multiple company, OD only or TP only"
                    onChange={(e, next) => {
                      if (next) handleTab(next);
                    }}
                    sx={CHOICE_TOGGLE_SX}
                  >
                  <ToggleButton value="single">Single company</ToggleButton>
                    {(multiProductPolicy || mode === "multiple") && (
                      <ToggleButton value="multiple">Multiple company</ToggleButton>
                    )}
                    {(coverChoice || coverage !== "both") && <ToggleButton value="od">OD only</ToggleButton>}
                    {(coverChoice || coverage !== "both") && <ToggleButton value="tp">TP only</ToggleButton>}
                  </ToggleButtonGroup>
                  <Typography sx={{ fontSize: 12, color: "#667085" }}>
                    {tab === "multiple"
                      ? "OD and TP are sold by two insurers, each with its own vehicle."
                      : tab === "od"
                        ? "Own damage only — sold by Reliance. Pick a Reliance Make & Model below; the customer's TP policy is asked on the next page."
                        : tab === "tp"
                          ? "Third party only, sold by one insurer — pick the Make & Model below, the company comes from it."
                          : "One insurer sells both OD and TP."}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Make & Model */}
            <Grid item xs={12} sm={6} md={mode === "multiple" ? 6 : 4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                {mode === "multiple" ? "Combined Make & Model *" : "Make & Model *"}
              </Typography>
              {mode === "multiple" ? (
                <Autocomplete
                  name="makeModelCombo"
                  disablePortal
                  className="AutoComplete_InputBox"
                  value={selectedCombo}
                  options={comboAutocompleteOptions}
                  onChange={(e, v) => {
                    if (!v) {
                      handleMakeModelChange(null);
                      return;
                    }
                    // A record rebuilt from a saved policy has no OD row to
                    // re-pick; it is already the current selection.
                    if (!v.odRow) return;
                    handleMakeModelChange(v.odRow, { combo: v });
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <Box sx={{ width: "100%", py: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: "#1E3A5F" }}>
                          {option.displayName}
                        </Typography>
                        <Typography sx={{ fontSize: "11.5px", color: "#475467", mt: 0.25 }}>
                          {option.odCompany} (OD) · {option.tpCompany} (TP)
                        </Typography>
                        {option.odRow && option.tpRow && (
                          <Typography sx={{ fontSize: "11px", color: "#667085", mt: 0.25 }}>
                            {option.odRow.make} {option.odRow.model} · {option.tpRow.make} {option.tpRow.model}
                            {option.tpRow.vehicleVariant ? ` ${option.tpRow.vehicleVariant}` : ""}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    !!option && !!value && String(option._id) === String(value._id)
                  }
                  getOptionLabel={(option) =>
                    option ? `${option.displayName} (${option.odCompany} OD / ${option.tpCompany} TP)` : ""
                  }
                  filterOptions={(options, { inputValue }) => {
                    const q = String(inputValue || "").trim().toLowerCase();
                    return q ? options.filter((o) => (o.searchText || "").includes(q)) : options;
                  }}
                  noOptionsText={
                    comboOptions.length === 0
                      ? "No Combined Make & Model created yet — ask the admin to add one"
                      : "No Combined Make & Model assigned to you"
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Search by name, company or vehicle" />
                  )}
                />
              ) : (
              <Autocomplete
                name="makeModel"
                disablePortal
                className="AutoComplete_InputBox"
                value={selectedMakeModel}
                options={autocompleteOptions}
                {...(odOnlyReliance ? { noOptionsText: "No Reliance Make & Model found — OD only is sold by Reliance only" } : {})}
                onInputChange={(event, newInputValue, reason) => {
                  // Only filter while the user is typing. On select/clear MUI also
                  // fires this with the chosen label, which would shrink the list
                  // to a single option — restore the full list instead.
                  if (reason === "input") {
                    filterMakeModelData(newInputValue);
                  } else {
                    filterMakeModelData("");
                  }
                }}
                onChange={(e, v) => {
                  handleMakeModelChange(v);
                }}
                renderOption={(props, option) => {
                  // Same resolver as the hint/auto-fill, so the range previewed
                  // here is the range actually applied on select.
                  const range = resolveIdvRange(option);
                  const specs = [
                    option.vehicleVariant,
                    option.cc ? `${option.cc} CC` : "",
                    option.fuelType,
                    option._disabledInMaster ? "Disabled in master" : "",
                  ].filter(Boolean);
                  // A row is one insurer's vehicle: its company is the badge.
                  const companyBadge = option.company;
                  return (
                    <li {...props} key={option._id}>
                      {/* Make & Model leads, company sits in a badge on the
                          right. It used to be the other way round — company
                          large and bold, make/model at 10px — which read as a
                          list of insurers rather than a list of vehicles. */}
                      <Box sx={{ width: "100%", py: 0.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "13.5px",
                              color: "#1E3A5F",
                            }}
                          >
                            {option.make} - {option.model}
                          </Typography>
                          {companyBadge && (
                            <Box
                              component="span"
                              sx={{
                                flexShrink: 0,
                                fontSize: "10px",
                                fontWeight: 700,
                                letterSpacing: "0.3px",
                                textTransform: "uppercase",
                                px: 0.9,
                                py: "2px",
                                borderRadius: "4px",
                                backgroundColor: "#1E3A5F14",
                                color: "#1E3A5F",
                              }}
                            >
                              {companyBadge}
                            </Box>
                          )}
                        </Box>
                        {specs.length > 0 && (
                          <Typography
                            sx={{ fontSize: "11.5px", color: "#475467", mt: 0.25 }}
                          >
                            {specs.join(" · ")}
                          </Typography>
                        )}
                        {range.from && range.to && (
                          <Typography
                            sx={{ fontSize: "11px", color: "#667085", mt: 0.25 }}
                          >
                            IDV {range.from} – {range.to}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                // A vehicle disabled in the master stays listed only because this
                // policy was saved with it — it cannot be picked again.
                getOptionDisabled={(option) => !!option?._disabledInMaster}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  const optionId = option._id?.toString();
                  const valueId = value._id?.toString();
                  return optionId === valueId;
                }}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  const base = option.label || `${option.make} - ${option.model}`;
                  // Variant and company go in the closed input too. Two rows
                  // can share a make and model and differ only by variant, so
                  // the bare label left them indistinguishable once selected —
                  // and company is hidden everywhere else on this form, so this
                  // is the operator's only confirmation of which insurer the
                  // policy is being raised for.
                  const companyText = option.company;
                  const extra = [option.vehicleVariant, companyText]
                    .filter(Boolean)
                    .join(" · ");
                  return extra ? `${base} (${extra})` : base;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search make, model, company or variant"
                  />
                )}
                filterOptions={(options) => options}
              />
              )}
              {routing.ODCompany && (
                <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>
                  {mode === "multiple"
                    ? `Company: ${routing.ODCompany} (OD) · ${routing.TPCompany} (TP) — from the Combined Make & Model`
                    : `Company: ${routing.ODCompany} — set in the Make & Model master`}
                </Typography>
              )}
              <div className="errorMessage mb-2 mt-2">
                {(mode === "multiple" ? errors.makeModelCombo || errors.makeModel : errors.makeModel) &&
                (touched.makeModel || touched.makeModelCombo) ? (
                  <div>
                    {mode === "multiple" ? errors.makeModelCombo || errors.makeModel : errors.makeModel}
                  </div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            {/* RTO City/Location */}
            <Grid item xs={12} sm={6} md={mode === "multiple" ? 6 : 4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                RTO City/Location *
              </Typography>
              <Autocomplete
                name="rtoCityLocation"
                className="AutoComplete_InputBox"
                disablePortal
                value={rtoValue}
                options={visibleRtoLocations}
                noOptionsText="No RTO City/Location assigned to you — add one from the RTO City Location page"
                onChange={(e, v) => {
                  // ONE atomic update, not three setFieldValue calls.
                  //
                  // Formik validates each setFieldValue against
                  // setIn(state.values, field, value), and within a single
                  // handler all three calls read the SAME stale snapshot. The
                  // last one (RTOCity) therefore revalidated with
                  // rtoCityLocation still empty and its result overwrote the
                  // good one — so the "RTO required" error stayed on screen
                  // after picking a value, until Next forced a fresh validate.
                  releaseSaved?.("rto"); // an edited policy stops keeping its saved zone / region / city
                  const next = {
                    ...values,
                    rtoCityLocation: v?.value || "",
                    RTORegion: v?.region || "",
                    RTOCity: v?.city || "",
                  };
                  if (formik?.setValues) {
                    formik.setValues(next);
                  } else {
                    setFieldValue("rtoCityLocation", next.rtoCityLocation);
                    setFieldValue("RTORegion", next.RTORegion);
                    setFieldValue("RTOCity", next.RTOCity);
                  }
                  if (setShowCalculationResult) setShowCalculationResult(false);
                }}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  return option.label || "";
                }}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select RTO City/Location"
                  />
                )}
              />
              {rtoZone && (
                // Same weight, colour AND gap as the IDV "Allowed range" beside it: directly under the field with a
                // small space (mt 0.5), the error line after it. As muted grey this read as fine print, but the zone
                // drives the premium — it has to be as legible as the range is. (It used to sit AFTER the error line
                // and be pulled back up with a negative margin, so it touched the dropdown.)
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#475467",
                    mt: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {keeps("zone") && zoneKeyFromName(rtoZone.zoneName) !== values.zone
                    ? `Zone ${values.zone} (saved with this policy — the RTO master now says ${rtoZone.zoneName})`
                    : `Zone: ${rtoZone.zoneName} (from the RTO master)`}
                </Typography>
              )}
              <div className="errorMessage mb-2 mt-2">
                {errors.rtoCityLocation && touched.rtoCityLocation ? (
                  <div>{errors.rtoCityLocation}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            {/* Asked ONLY when the selected RTO has no zone on the master —
                the base rate differs per zone, so it cannot just be assumed. */}
            {zoneNeedsChoice && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                  Zone *
                </Typography>
                <Autocomplete
                  className="AutoComplete_InputBox"
                  disablePortal
                  // Active zones only — a deleted zone must not be offered.
                  // zoneList keeps the inactive ones too, because an RTO may
                  // still reference one and it has to resolve for display.
                  options={zoneList.filter(
                    (z) => z.isActive !== false && zoneKeyFromName(z.zoneName)
                  )}
                  value={
                    zoneList.find(
                      (z) => zoneKeyFromName(z.zoneName) === values.zone
                    ) || null
                  }
                  onChange={(e, v) => {
                    setFieldValue("zone", zoneKeyFromName(v?.zoneName) || "");
                    if (setShowCalculationResult) setShowCalculationResult(false);
                  }}
                  // Name only — the rate is master data, not something to pick by.
                  getOptionLabel={(option) => option?.zoneName || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?._id) === String(value?._id)
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select Zone" />
                  )}
                />
                <Typography sx={{ fontSize: 12, color: "#b54708", mt: 0.5 }}>
                  This RTO has no zone set — pick one so the premium uses the
                  right base rate.
                </Typography>
                <div className="errorMessage mb-2 mt-2">
                  {errors.zone && touched.zone ? <div>{errors.zone}</div> : " "}
                </div>
              </Grid>
            )}

            {/* Selling price and IDV: their own section below (Single company); Multiple company / OD only have them in
                the OD panel below; TP only has none (nothing is insured for own damage) */}

            {/* Discount: Multiple company has it in the OD panel below; it prices own damage, so TP only has none */}
            {mode !== "multiple" && !showPartPanel && (
            <Grid item xs={12} sm={6} md={4}>
              {discountField}
            </Grid>
            )}

            {/* Manufacturing Year */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Manufacturing Year *
              </Typography>
              <Field
                name="manufacturingYear"
                placeholder="Manufacturing Year"
                type="number"
                className="textField w-100"
                // The calculator prices vehicle age from it, so the premium on
                // screen is no longer valid.
                onChange={(e) => {
                  setFieldValue("manufacturingYear", e.target.value);
                  if (setShowCalculationResult) setShowCalculationResult(false);
                }}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.manufacturingYear && touched.manufacturingYear ? (
                  <div>{errors.manufacturingYear}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            {/* Manufacturing Month */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Manufacturing Month *
              </Typography>
              <Autocomplete
                name="manufacturingMonth"
                className="AutoComplete_InputBox"
                disablePortal
                value={monthValue}
                options={months}
                onChange={(e, v) => {
                  setFieldValue("manufacturingMonth", v?.value || "");
                  if (setShowCalculationResult) setShowCalculationResult(false);
                }}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  return option.label || "";
                }}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Manufacturing Month" />
                )}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.manufacturingMonth && touched.manufacturingMonth ? (
                  <div>{errors.manufacturingMonth}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            {/* Purchase Date */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Purchase Date *
              </Typography>
              <DatePicker
                name="purchaseDate"
                className="Date_Picker w-100"
                value={values.purchaseDate ? moment(values.purchaseDate) : null}
                onChange={(date, dateString) => {
                  const value = date ? date.toDate() : null;
                  // Atomic, for the same reason as the RTO field above: both of
                  // these are required, and two setFieldValue calls in one
                  // handler validate against the same stale snapshot, leaving
                  // the first field's error on screen.
                  //
                  // Registration Date is no longer asked for separately — it is
                  // always the same day. Keeping the stored field in step means
                  // the payload, the preview and the automation are unaffected.
                  if (formik?.setValues) {
                    formik.setValues({
                      ...values,
                      purchaseDate: value,
                      registrationDate: value,
                    });
                  } else {
                    setFieldValue("purchaseDate", value);
                    setFieldValue("registrationDate", value);
                  }
                }}
                placeholder="Purchase Date (DD/MM/YYYY)"
                format="DD/MM/YYYY"
                getPopupContainer={(trigger) => trigger.parentElement}
                changeOnBlur={false}
                popupStyle={{ zIndex: 9999 }}
                showToday={true}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.purchaseDate && touched.purchaseDate ? (
                  <div>{errors.purchaseDate}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            {/* Multiple company: the two insurers, side by side. LEFT is OD (own
                damage): the vehicle, IDV, discount and Zero Depreciation, then
                Total A. RIGHT is TP (third party): the vehicle and TPPD, then
                Total B (third party alone). PA Cover, with its own company, and
                Net Premium / GST / Final Premium follow below. */}
            {/* OD only / TP only: ONE panel, the same one Multiple company has for that side. The insurer and the vehicle
                come from the Make & Model picked below, as in Single company. */}
            {showPartPanel && (
              <Grid item xs={12} className="op-panels" sx={{ mt: 1, mb: 3 }}>
                <Grid container spacing={3} alignItems="stretch">
                  <Grid item xs={12} md={6}>
                    <CompanyPanel
                      sideKey={coverage}
                      title={coverage === "od" ? "OD · Own Damage" : "TP · Third Party"}
                      company={coverage === "od" ? routing.ODCompany : routing.TPCompany}
                      vehicle={
                        selectedMakeModel
                          ? {
                              name: [selectedMakeModel.make, selectedMakeModel.model].filter(Boolean).join(" "),
                              variant: selectedMakeModel.vehicleVariant || "",
                              cc: selectedMakeModel.cc,
                            }
                          : null
                      }
                      idvRange={coverage === "od" ? idvRangeHint : ""}
                      total={
                        totals
                          ? coverage === "od"
                            ? { label: "Total A [OD Premium]", amount: totals.totalA }
                            : { label: "Total B [TP Premium]", amount: totals.tpPremium }
                          : null
                      }
                    >
                      {coverage === "od" ? (
                        <>
                          <Box>{priceField}</Box>
                          <Box>{idvField}</Box>
                          <Box>{discountField}</Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "stretch",
                              gap: 1,
                              pt: 1.5,
                              borderTop: "1px solid #F2F4F7",
                              "& > .MuiBox-root": { width: "100% !important" },
                            }}
                          >
                            {zeroDepRow}
                          </Box>
                        </>
                      ) : (
                        <>
                          {tppdOffered ? (
                            <Box sx={{ pt: 0.5, "& > .MuiBox-root": { width: "100% !important" } }}>{tppdRow}</Box>
                          ) : (
                            <Typography sx={{ fontSize: 13, color: "#667085" }}>
                              TPPD Restrict is not offered for this vehicle.
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 12, color: "#667085" }}>
                            The third party premium follows the vehicle's CC; it does not depend on an IDV or a discount.
                          </Typography>
                        </>
                      )}
                    </CompanyPanel>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {mode === "multiple" && !showSidePanels && (
              <Grid item xs={12} className="op-panels" sx={{ mt: 1, mb: 3 }}>
                <Typography
                  sx={{ fontSize: 13, color: "#667085", border: "1px dashed #D0D5DD", borderRadius: "8px", p: 2, textAlign: "center" }}
                >
                  Choose a Combined Make & Model to enter the IDV, discount and cover for the OD insurer (left) and the TP
                  insurer (right).
                </Typography>
              </Grid>
            )}
            {showSidePanels && (
              <Grid item xs={12} className="op-panels" sx={{ mt: 1, mb: 3 }}>
                <Grid container spacing={3} alignItems="stretch">
                  <Grid item xs={12} md={6}>
                    <CompanyPanel
                      sideKey="od"
                      title="OD · Own Damage"
                      company={routing.ODCompany}
                      vehicle={{
                        name: [selectedMakeModel?.make, selectedMakeModel?.model].filter(Boolean).join(" "),
                        variant: selectedMakeModel?.vehicleVariant || "",
                        cc: selectedMakeModel?.cc,
                      }}
                      idvRange={idvRangeHint}
                      total={totals ? { label: "Total A [OD Premium]", amount: totals.totalA } : null}
                    >
                      <Box>{priceField}</Box>
                      <Box>{idvField}</Box>
                      <Box>{discountField}</Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          gap: 1,
                          pt: 1.5,
                          borderTop: "1px solid #F2F4F7",
                          "& > .MuiBox-root": { width: "100% !important" },
                        }}
                      >
                        {zeroDepRow}
                      </Box>
                    </CompanyPanel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CompanyPanel
                      sideKey="tp"
                      title="TP · Third Party"
                      company={routing.TPCompany}
                      vehicle={
                        tpRow
                          ? {
                              name: [tpRow.make, tpRow.model].filter(Boolean).join(" "),
                              variant: tpRow.vehicleVariant || "",
                              cc: tpRow.cc,
                            }
                          : values._legacyMultiple && selectedMakeModel
                            ? {
                                name: [selectedMakeModel.make, selectedMakeModel.model].filter(Boolean).join(" "),
                                variant: selectedMakeModel.vehicleVariant || "",
                                cc: selectedMakeModel.cc,
                              }
                            : null
                      }
                      total={
                        totals
                          ? {
                              label: "Total B [TP Premium]",
                              // Third party alone. The engine carries PA Cover inside its Total B;
                              // PA is its own company's, so it is on its own line under the panels.
                              amount: totals.tpPremium,
                            }
                          : null
                      }
                    >
                      {tppdOffered ? (
                        <Box sx={{ pt: 0.5, "& > .MuiBox-root": { width: "100% !important" } }}>{tppdRow}</Box>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: "#667085" }}>
                          TPPD Restrict is not offered for this vehicle.
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 12, color: "#667085" }}>
                        {tpRow
                          ? `The third party premium follows this vehicle's CC (${tpRow.cc} CC); it does not depend on the IDV or the discount.`
                          : "Sold before Combined Make & Model: the TP insurer uses the same vehicle as the OD insurer. Pick a Combined Make & Model to give it a vehicle of its own."}
                      </Typography>
                    </CompanyPanel>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {coverageBeside && !hidePaCover ? (
              <Grid item xs={12} className="op-left-span">
                {coverageSection}
              </Grid>
            ) : null}

            {/* Registration Date is intentionally not rendered: it is always
                the same as Purchase Date, so asking twice only invited
                mismatches. The value is mirrored above. */}
          </Grid>
      </FormSection>

      {/* Selling price and IDV in a section of their own (the owner, 2026-10-27) */}
      {idvInOwnSection && (
        <FormSection
          title="IDV & Selling Price"
          description="Type the selling price and the IDV is worked out; the IDV can be typed over inside the Make & Model range."
          action={idvCalculatorButton}
          testId="section-idv"
        >
          <Grid container className="op-fields-grid" columnSpacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              {priceField}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {idvField}
            </Grid>
          </Grid>
        </FormSection>
      )}

      {/* Premium Summary moved to bottom */}

      {/* Coverage options: here, unless they sit in the left column beside the OD / TP panels (above) */}
      {!coverageBeside && !hidePaCover && coverageSection}

      {/* PA Cover Nominee Details - Conditional (duplicate disabled) */}
      {false && (
        <>
          <Grid item xs={12}>
            <Divider
              sx={{
                backgroundColor: "#EAECF0",
                borderWidth: 1,
                mt: 2,
              }}
            />
          </Grid>

          <Grid container sx={{ alignItems: "center", padding: "0 5px" }}>
            <Grid item xs={12} sm={10.5} sx={{ mb: 1 }}>
              <Grid container sx={{ p: 2, pt: 4, mb: -3 }} columnSpacing={6}>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      ...SECTION_TITLE_SX,
                      mb: 3,
                    }}
                  >
                    PA Cover Details
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                    Nominee Name *
                  </Typography>
                  <Field
                    name="nomineeName"
                    placeholder="Nominee Name"
                    className="textField w-100"
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^a-zA-Z\s]/g, "")
                        .toUpperCase();
                      setFieldValue("nomineeName", value);
                    }}
                  />
                  <div className="errorMessage mb-2 mt-2">
                    {errors.nomineeName && touched.nomineeName ? (
                      <div>{errors.nomineeName}</div>
                    ) : (
                      " "
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                    Nominee Relation *
                  </Typography>
                  <Autocomplete
                    name="nomineeRelation"
                    className="AutoComplete_InputBox"
                    disablePortal
                    value={nomineeValue}
                    options={values.company?.toLowerCase() === "national" ? nationalNomineeRelations : relianceNomineeRelations}
                    onChange={(e, v) => {
                      setFieldValue("nomineeRelation", v?.value || "");
                    }}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      return option.label || "";
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Nominee Relation" />
                    )}
                  />
                  <div className="errorMessage mb-2 mt-2">
                    {errors.nomineeRelation && touched.nomineeRelation ? (
                      <div>{errors.nomineeRelation}</div>
                    ) : (
                      " "
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                    Nominee Date of Birth *
                  </Typography>
                  <DatePicker
                    name="nomineeDob"
                    className="Date_Picker w-100"
                    value={values.nomineeDob ? moment(values.nomineeDob) : null}
                    onChange={(date, dateString) => {
                      if (date) {
                        setFieldValue("nomineeDob", date.toDate());
                      } else {
                        setFieldValue("nomineeDob", null);
                      }
                    }}
                    placeholder="Nominee Date of Birth (DD/MM/YYYY)"
                    format="DD/MM/YYYY"
                    getPopupContainer={(trigger) => trigger.parentElement}
                    changeOnBlur={false}
                    popupStyle={{ zIndex: 9999 }}
                    showToday={false}
                    disabledDate={(current) => {
                      // Disable future dates for nominee DOB
                      return current && current > moment().endOf("day");
                    }}
                  />
                  <div className="errorMessage mb-2 mt-2">
                    {errors.nomineeDob && touched.nomineeDob ? (
                      <div>{errors.nomineeDob}</div>
                    ) : (
                      " "
                    )}
                  </div>
                  {/* Financier details moved out of PA Cover */}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      {/* Bottom Premium Summary */}
      {showCalculationResult && (
        <FormSection
          ref={premiumRef}
          title="Premium"
          description="Worked out from the details above."
          testId="section-premium"
          // clear of the fixed navbar when scrolled to
          sx={{ scrollMarginTop: "120px" }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Grid container columnSpacing={3}>
              {totals ? (
                // Total A and Total B are in the panels above; this is PA Cover with the company that
                // sells it, then what the calculator ends on.
                <Grid item xs={12} sx={{ mb: 1 }}>
                  <Box data-testid="premium-totals">
                    <PremiumFooter
                      standalone
                      pa={
                        footer.pa > 0
                          ? { label: `PA Cover — ${routing.PACompany || "PA company"}`, amount: footer.pa }
                          : null
                      }
                      net={footer.net}
                      gst={footer.gst}
                      final={footer.final}
                    />
                  </Box>
                  {/* Only an old saved premium can get here: the engine adds A and B itself. */}
                  {totals.mismatchPaise !== 0 && (
                    <Typography data-testid="premium-totals-mismatch" sx={{ fontSize: 12.5, color: "#B42318", mt: 0.75 }}>
                      Total A + Total B{totals.pa > 0 ? " + PA Cover" : ""} does not match the Net Premium: it differs by ₹
                      {(Math.abs(totals.mismatchPaise) / 100).toFixed(2)}.
                    </Typography>
                  )}
                </Grid>
              ) : premiumSplitView?.isSplit ? (
                <>
                  {/* Multi-company: one line per part, labelled with the
                      insurer that sells it, then who is paid what. */}
                  {premiumSplitView.legs.map((leg) => (
                    <React.Fragment key={leg.key}>
                      <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                        {leg.company} — {leg.label}
                      </Grid>
                      <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                        : ₹ {leg.total.toFixed(2)}
                      </Grid>
                    </React.Fragment>
                  ))}
                  <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                    Total
                  </Grid>
                  <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                    : ₹{premiumSplitView.grandTotal.toFixed(2)}
                  </Grid>
                  <Grid item xs={12} mb={1}>
                    <Typography sx={{ fontSize: 13, color: "#475467" }}>
                      Pay to:{" "}
                      {premiumSplitView.byCompany
                        .map(
                          (c) =>
                            `${c.company} ₹${c.charge.toFixed(2)} (${describeParts(c.parts)})${
                              c.charge !== c.total ? " excl. GST" : ""
                            }`
                        )
                        .join("  ·  ")}
                    </Typography>
                  </Grid>
                </>
              ) : (
              <>
              <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                Motor Premium
              </Grid>
              <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                : ₹ {motorOnlyPremium.toFixed(2)}
              </Grid>
              {/* Only when PA Cover is actually selected — this used to render
                  and be added to the Total regardless, so an amount left over
                  from before unticking still inflated the premium. */}
              {values.paCover && (
                <>
                  <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                    CPA (With GST)
                  </Grid>
                  <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                    : ₹ {cpaWithGst.toFixed(2)}
                  </Grid>
                </>
              )}
              <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                Total
              </Grid>
              <Grid item xs={6} sx={{ fontWeight: 700 }} mb={2}>
                : ₹{finalPremiumWithPaCover.toFixed(2)}
              </Grid>
              </>
              )}
              <Grid item xs={12}>
                {savedPremiumInUse ? (
                  // No calculation behind these figures, so there is no breakup to show.
                  <Typography sx={{ fontSize: 12, color: "#667085", mt: 1 }}>
                    Saved premium — recalculated only if you change a pricing
                    field or use the latest master values.
                  </Typography>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setOpenBreakup(true)}
                    sx={{ ...TOOL_BUTTON_SX, fontSize: "13px", mt: 1.5 }}
                  >
                    View Premium Breakup
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </FormSection>
      )}

      {/* Premium Breakup Dialog */}
      <Dialog
        open={openBreakup}
        onClose={() => setOpenBreakup(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "14px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: 18,
            fontFamily: FONT,
            pb: 2,
            borderBottom: "1px solid #EAECF0",
            color: NAVY,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          Premium Calculation
          {/* Same figures, full calculator — in a new tab so this breakup stays
              open to compare against. For two vehicles the TP vehicle's CC is
              handed over too, so that page reproduces Total B as well. */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={handleOpenInCalculator}
            sx={{
              ...SECONDARY_BUTTON_SX,
              fontSize: "13px",
              whiteSpace: "nowrap",
            }}
          >
            Open in Calculator
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {/* The same breakdown card the MotorCalculation page shows, fed from
              this policy's own calculation. Both screens run the identical
              engine, so the numbers match row for row. */}
          <MotorCalculationCard calculationResult={calculationResult} />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #EAECF0" }}>
          <Button
            onClick={() => setOpenBreakup(false)}
            variant="contained"
            sx={{
              ...PRIMARY_BUTTON_SX,
              px: 2.5,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BasicPolicyDetailsStep;
